import { DEFAULT_STYLE_PRESETS, DEFAULT_CONTENT_FLOWS } from './features/presets/defaults';
import React, { useState, useEffect } from 'react';
import {
  Film,
  PlusCircle,
  Settings,
  Layers,
  Sparkles,
  CheckCircle,
  XCircle,
  Upload,
  Play,
  Activity,
  ShieldCheck,
  Video,
  FileText,
  Trash2,
  Palette,
  Smartphone,
  Monitor,
  Square,
  Lock,
  Unlock,
  Users,
  Target,
  ShoppingBag,
  BookOpen,
  TrendingUp,
  Flame,
  CheckCircle2,
  RefreshCw,
  AlertCircle,
  Loader2,
  Image,
  Pause,
  SkipForward,
  SkipBack,
  Eye,
  Mic,
  Volume2,
  Download,
  Clock,
  Save,
} from 'lucide-react';
import { PromptPresetEditor, type EditablePreset } from './components/PromptPresetEditor';
import { VideoPlayer } from './components/VideoPlayer';
import { JobsDashboard } from './components/JobsDashboard';
import { SettingsView } from './components/SettingsView';
import { EarlierSceneReferencePicker } from './components/earlier-scene-reference-picker';
import { reconcileSelection, usableImageModels, usableVideoModels, videoModelsForMode, type LocalImageModel, type LocalVideoModel, type LocalVoice } from './features/local-media/catalog';
import {
  Job,
  listJobs,
  createJob,
  getJob,
  deleteJob,
  generateScript,
  generateSceneImage,
  generateSceneAudio,
  generateAllAudios,
  listScenes,
  patchScene,
  toggleSceneApproval,
  approveAllScenes,
  decideScript,
  decideScenes,
  dispatchRender,
  pollRender,
  uploadAsset,
  listProviderConnections,
  createProviderConnection,
  deleteProviderConnection,
  fetchHealth,
  getLocalMediaModels,
  getLocalMediaImageModels,
  getLocalMediaVideoModels,
  getLocalMediaAudioVoices,
  suggestMotionPrompt,
  setSceneReferenceImage,
  setSceneVisualImage,
  generateSceneVideo,
  listAssets,
  BASE_URL,
  apiFetch,
  Scene,
  SceneRevision,
} from './api/client';

export const RATIO_PRESETS = [
  { id: '9:16', label: '9:16 แนวตั้ง', resolution: '1080 × 1920', icon: Smartphone, desc: 'TikTok, Shorts, Reels' },
  { id: '16:9', label: '16:9 แนวนอน', resolution: '1920 × 1080', icon: Monitor, desc: 'YouTube, Web Video' },
  { id: '1:1', label: '1:1 จัตุรัส', resolution: '1080 × 1080', icon: Square, desc: 'Facebook และ IG Feed' },
];

export const DURATION_PRESETS = [
  { seconds: 15, label: '15 วินาที', desc: 'สั้นกระชับ 2-3 ฉาก' },
  { seconds: 30, label: '30 วินาที', desc: 'มาตรฐาน 3-5 ฉาก' },
  { seconds: 60, label: '60 วินาที', desc: 'เล่าเรื่อง 5-8 ฉาก' },
  { seconds: 90, label: '90 วินาที', desc: 'เจาะลึก 8-12 ฉาก' },
];

export const AUDIENCE_PRESETS = [
  'คนทั่วไป',
  'คนรุ่นใหม่และวัยรุ่น',
  'วัยทำงานและพนักงานออฟฟิศ',
  'เจ้าของธุรกิจและฟรีแลนซ์',
  'นักช้อปออนไลน์',
  'สายเทคโนโลยีและครีเอเตอร์',
];

export const OBJECTIVE_PRESETS = [
  'สร้างความรู้และให้สาระ',
  'เพื่อความบันเทิง',
  'กระตุ้นยอดขายและปิดการขาย',
  'สร้างการจดจำแบรนด์',
  'สร้างแรงบันดาลใจ',
  'ตอบโจทย์และแก้ปัญหา',
];

export const CTA_PRESETS = [
  'กดติดตามเพื่อไม่พลาดคลิปใหม่',
  'สั่งซื้อได้ที่ตะกร้าด้านล่างเลย',
  'กดบันทึกและแชร์ให้เพื่อนที่สนใจ',
  'คอมเมนต์บอกความเห็นของคุณด้านล่าง',
  'คลิกลิงก์ที่หน้าโปรไฟล์เพื่อดูโปรพิเศษ',
];

export const GEMINI_SCRIPT_MODELS = [
  { id: 'gemini-3.5-flash', name: 'Gemini 3.5 Flash', desc: 'Google Gemini 3.5 Flash' },
  { id: 'gemini-3.6-flash', name: 'Gemini 3.6 Flash', desc: 'Google Gemini 3.6 Flash' },
  { id: 'gemini-3.7-flash', name: 'Gemini 3.7 Flash', desc: 'Google Gemini 3.7 Flash' },
  { id: 'gemini-3.8-flash', name: 'Gemini 3.8 Flash', desc: 'Google Gemini 3.8 Flash แนะนำ' },
];

export const FLUX_IMAGE_MODELS = [
  { id: 'black-forest-labs/FLUX.1.1-pro', name: 'FLUX 1.1 Pro', desc: 'คุณภาพสูงสุด คมชัดสมจริง' },
  { id: 'black-forest-labs/FLUX.2-dev', name: 'FLUX 2 Dev', desc: 'โมเดล FLUX 2 Dev' },
  { id: 'black-forest-labs/FLUX.2-flex', name: 'FLUX 2 Flex', desc: 'โมเดล FLUX 2 Flex' },
  { id: 'black-forest-labs/FLUX.2-pro', name: 'FLUX 2 Pro', desc: 'โมเดล FLUX 2 Pro' },
  { id: 'black-forest-labs/FLUX.2-max', name: 'FLUX 2 Max', desc: 'โมเดล FLUX 2 Max ความละเอียดสูงสุด' },
];

export interface ProviderItem {
  id: string;
  name: string;
  category: 'cloud' | 'local';
  badge: string;
  desc: string;
  disabled?: boolean;
}

export const SCRIPT_PROVIDERS: ProviderItem[] = [
  { id: 'google_web', name: 'Google Gemini Pro (Browser)', category: 'local', badge: 'Browser', desc: 'แต่งบทผ่าน Google Gemini Pro ในเบราว์เซอร์อัตโนมัติ ไม่ต้องใช้ API Key' },
  { id: 'google', name: 'Google Gemini (API Key)', category: 'cloud', badge: 'Cloud', desc: 'คลาวด์ความเร็วสูง ฉลาดและแม่นยำ (ต้องระบุ API Key ในตั้งค่า)' },
  { id: 'ollama', name: 'Local LLM (LM Studio / Ollama)', category: 'local', badge: 'Local', desc: 'รันโมเดลภาษาในเครื่อง (LM Studio / Ollama) ฟรีและเป็นส่วนตัว' },
  { id: 'openai', name: 'OpenAI GPT', category: 'cloud', badge: 'Cloud', desc: 'เชื่อมต่อ OpenAI เร็วๆ นี้', disabled: true },
];

export interface DynamicLocalModel {
  id: string;
  name: string;
  source: string;
  desc?: string;
  size?: number;
}

export const IMAGE_PROVIDERS: ProviderItem[] = [
  { id: 'google_flow', name: 'Google Flow Browser', category: 'local', badge: 'Browser', desc: 'สร้างภาพด้วย Google Flow ผ่านเบราว์เซอร์อัตโนมัติ' },
  { id: 'gemini_web', name: 'Google Gemini Browser', category: 'local', badge: 'Browser', desc: 'สร้างภาพด้วย Google Gemini Browser ผ่านเบราว์เซอร์อัตโนมัติ' },
  { id: 'together', name: 'Together AI FLUX', category: 'cloud', badge: 'Cloud', desc: 'โมเดล FLUX คุณภาพระดับสตูดิโอบนคลาวด์' },
  { id: 'local_sd', name: 'Local ComfyUI / SD', category: 'local', badge: 'Local', desc: 'รันสร้างภาพผ่าน ComfyUI (http://127.0.0.1:8188) ในเครื่อง' },
  { id: 'midjourney', name: 'Midjourney', category: 'cloud', badge: 'Cloud', desc: 'เชื่อมต่อ Midjourney API เร็วๆ นี้', disabled: true },
];

export const AUDIO_PROVIDERS: ProviderItem[] = [
  { id: 'local_edge_tts', name: 'Edge TTS (Local Fast)', category: 'local', badge: 'Local', desc: 'สร้างเสียงภาษาไทยธรรมชาติทันที รวดเร็ว ไม่กิน GPU' },
  { id: 'local_f5_tts', name: 'ThonburianTTS Voice Clone', category: 'local', badge: 'Local', desc: 'เสียงโคลนภาษาไทยสมจริงคุณภาพสูง พัฒนาจาก F5-TTS รันในเครื่อง' },
  { id: 'elevenlabs', name: 'ElevenLabs', category: 'cloud', badge: 'Cloud', desc: 'เสียงพากย์คลาวด์ระดับสตูดิโอ เร็วๆ นี้', disabled: true },
];

export const ELEVENLABS_VOICES = [
  { id: 'rachel', name: 'Rachel (หญิง) • ElevenLabs' },
  { id: 'adam', name: 'Adam (ชาย) • ElevenLabs' },
];

export const RENDER_PROVIDERS: ProviderItem[] = [
  { id: 'local_remotion', name: 'Local Media Remotion Engine', category: 'local', badge: 'Local', desc: 'เรนเดอร์วิดีโอระดับเฟรมผ่านเครื่องคอมพิวเตอร์ของคุณ' },
  { id: 'cloud_kling', name: 'Cloud GPU Render', category: 'cloud', badge: 'Cloud', desc: 'เรนเดอร์บนเซิร์ฟเวอร์คลาวด์ความเร็วสูง เร็วๆ นี้', disabled: true },
];

export const VIDEO_PROVIDERS: ProviderItem[] = [
  { id: 'google_flow', name: 'Google Flow Browser', category: 'local', badge: 'Browser', desc: 'สร้างวิดีโอด้วย Google Flow ผ่านเบราว์เซอร์อัตโนมัติ' },
  { id: 'gemini_web', name: 'Google Gemini Browser', category: 'local', badge: 'Browser', desc: 'สร้างวิดีโอผ่านบัญชี Gemini ที่ล็อกอินไว้' },
  { id: 'comfyui', name: 'Local ComfyUI Video', category: 'local', badge: 'Local', desc: 'สร้างวิดีโอด้วยโมเดลที่ Local Media ตรวจพบใน ComfyUI' },
  { id: 'kling', name: 'Kling AI Video (v1.5 Pro)', category: 'cloud', badge: 'Cloud', desc: 'ยังไม่มี adapter ที่ใช้งานได้', disabled: true },
  { id: 'runway', name: 'Runway Gen-3 Alpha', category: 'cloud', badge: 'Cloud', desc: 'ยังไม่มี adapter ที่ใช้งานได้', disabled: true },
  { id: 'luma', name: 'Luma Dream Machine', category: 'cloud', badge: 'Cloud', desc: 'ยังไม่มี adapter ที่ใช้งานได้', disabled: true },
];


type SceneVideoPath = 't2v' | 'generated' | 'reference';
type SceneImageMode = 't2i' | 'i2i';

function SceneImageModePicker({ referenceUrl, value, provider, disabled = false, onChange }: {
  referenceUrl?: string | null;
  value: SceneImageMode;
  provider: string;
  disabled?: boolean;
  onChange: (mode: SceneImageMode) => void;
}) {
  if (!referenceUrl) return null;
  return (
    <div className="space-y-1.5">
      <div className="text-[11px] text-slate-300">เลือกวิธีสร้างภาพ</div>
      <div className="flex flex-wrap gap-2">
        {([{ id: 't2i', label: 'ข้อความ → ภาพ' }, { id: 'i2i', label: 'ข้อความ + ภาพ Ref → ภาพ' }] as const).map((choice) => (
          <button key={choice.id} type="button" disabled={disabled} onClick={() => onChange(choice.id)} aria-pressed={value === choice.id}
            className={`rounded border px-2 py-1.5 text-[11px] ${value === choice.id ? 'border-sky-400 bg-sky-950/60 text-sky-200' : 'border-slate-700 bg-slate-950 text-slate-300 hover:border-slate-500'}`}>
            {choice.label}
          </button>
        ))}
      </div>
      {value === 'i2i' && !['local_sd', 'google_flow', 'gemini_web'].includes(provider) &&
        <div className="text-[11px] text-amber-400">ผู้ให้บริการนี้ยังไม่รองรับภาพอ้างอิง</div>}
    </div>
  );
}

function SceneVideoPathPicker({ imageUrl, referenceUrl, value, disabled = false, onChange }: {
  imageUrl?: string | null;
  referenceUrl?: string | null;
  value: SceneVideoPath;
  disabled?: boolean;
  onChange: (path: SceneVideoPath) => void;
}) {
  const choices: Array<{ id: SceneVideoPath; label: string; image?: string | null }> = [
    { id: 't2v', label: 'ข้อความ → วิดีโอ' },
    ...(imageUrl ? [{ id: 'generated' as const, label: 'ภาพฉาก → วิดีโอ', image: imageUrl }] : []),
    ...(referenceUrl ? [{ id: 'reference' as const, label: 'ภาพ Ref → วิดีโอ', image: referencePreviewUrl(referenceUrl) }] : []),
  ];
  return (
    <div className="space-y-1.5">
      <div className="text-[11px] text-slate-300">เลือกภาพต้นทางของวิดีโอ</div>
      <div className="flex flex-wrap gap-2">
        {choices.map((choice) => (
          <button key={choice.id} type="button" disabled={disabled} onClick={() => onChange(choice.id)}
            aria-pressed={value === choice.id}
            className={`flex items-center gap-1.5 rounded border px-2 py-1.5 text-[11px] ${value === choice.id ? 'border-sky-400 bg-sky-950/60 text-sky-200' : 'border-slate-700 bg-slate-950 text-slate-300 hover:border-slate-500'}`}>
            {choice.image && <img src={choice.image} alt="" className="h-7 w-7 rounded object-cover" />}
            {choice.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function referencePreviewUrl(value: string): string {
  if (/^https?:\/\//i.test(value)) return value;
  const objectKey = value.replace(/^\/?(?:api\/)?r2\//, '').replace(/^\//, '');
  return `${BASE_URL}/api/r2/${objectKey.split('/').map(encodeURIComponent).join('/')}`;
}

export const VIDEO_QUALITY_PRESETS = [
  { id: '1080p', name: '1080p (Full HD)', desc: 'งานจริง คมชัดสูงสุดระดับโปรดักชัน' },
  { id: '720p', name: '720p (HD)', desc: 'มาตรฐาน คมชัดปานกลาง เรนเดอร์รวดเร็ว' },
  { id: '480p_draft', name: '480p (Draft / Fast Preview)', desc: 'พรีวิวเร็วสุด สำหรับตรวจงานก่อนไฟนอล' },
];

export const VIDEO_FPS_OPTIONS = [
  { id: 30, name: '30 fps (มาตรฐานวิดีโอทั่วไป)' },
  { id: 60, name: '60 fps (ลื่นไหลสูง High Frame Rate)' },
];

export const VIDEO_BITRATE_PRESETS = [
  { id: 18, name: 'High Quality (CRF 18 • คมชัดพิเศษ)' },
  { id: 23, name: 'Standard (CRF 23 • มาตรฐานสมดุล)' },
  { id: 28, name: 'Draft (CRF 28 • ไฟล์เล็ก เรนเดอร์เร็ว)' },
];

export const App: React.FC = () => {
  const [currentTab, setCurrentTab] = useState<'jobs' | 'new_job' | 'review' | 'settings'>('jobs');
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [selectedJobDetail, setSelectedJobDetail] = useState<any>(null);
  const [scenes, setScenes] = useState<Scene[]>([]);
  const [sceneRevisions, setSceneRevisions] = useState<SceneRevision[]>([]);
  const [approvedSceneIds, setApprovedSceneIds] = useState<Set<string>>(new Set());
  const [scriptApproved, setScriptApproved] = useState(false);
  const [previewSnapshotId, setPreviewSnapshotId] = useState<string | null>(null);
  const [previewOutputId, setPreviewOutputId] = useState<string | null>(null);
  const [previewVideoUrl, setPreviewVideoUrl] = useState<string | null>(null);
  const [isRenderingPreview, setIsRenderingPreview] = useState(false);

  // Provider & Model Selection states
  const [selectedScriptProvider, setSelectedScriptProvider] = useState<string>('google_web');
  const [selectedGeminiModel, setSelectedGeminiModel] = useState<string>('gemini-3.8-flash');
  const [selectedOllamaModel, setSelectedOllamaModel] = useState<string | null>(null);
  const [dynamicLocalModels, setDynamicLocalModels] = useState<DynamicLocalModel[]>([]);
  const [isLoadingLocalModels, setIsLoadingLocalModels] = useState(false);

  const [selectedImageProvider, setSelectedImageProvider] = useState<string>('google_flow');
  const [selectedFluxModel, setSelectedFluxModel] = useState<string>('black-forest-labs/FLUX.1.1-pro');
  const [selectedLocalImageModel, setSelectedLocalImageModel] = useState<string | null>(null);
  const [dynamicLocalImageModels, setDynamicLocalImageModels] = useState<LocalImageModel[]>([]);
  const [isLoadingLocalImageModels, setIsLoadingLocalImageModels] = useState(false);
  const [isComfyOnline, setIsComfyOnline] = useState<boolean | null>(null);
  const [isLoadingLocalVideoModels, setIsLoadingLocalVideoModels] = useState(false);
  const [isLoadingLocalVoices, setIsLoadingLocalVoices] = useState(false);
  const [localVoiceCatalog, setLocalVoiceCatalog] = useState<Record<string, LocalVoice[]>>({});

  const [selectedAudioProvider, setSelectedAudioProvider] = useState<string>('local_edge_tts');
  const [selectedVoice, setSelectedVoice] = useState<string | null>(null);

  const [selectedRenderProvider, setSelectedRenderProvider] = useState<string>('local_remotion');
  const [newJobScriptModel, setNewJobScriptModel] = useState<string>('gemini-3.8-flash');

  // Generation & Status states
  const [isCreatingJob, setIsCreatingJob] = useState(false);
  const [isGeneratingScript, setIsGeneratingScript] = useState(false);
  const [generatingImageSceneId, setGeneratingImageSceneId] = useState<string | null>(null);
  const [isGeneratingAllImages, setIsGeneratingAllImages] = useState(false);
  const [generatingAudioSceneId, setGeneratingAudioSceneId] = useState<string | null>(null);
  const [isGeneratingAllAudios, setIsGeneratingAllAudios] = useState(false);
  const [scriptGenError, setScriptGenError] = useState<string | null>(null);
  const [scriptGenSuccess, setScriptGenSuccess] = useState<string | null>(null);
  const [imageGenError, setImageGenError] = useState<string | null>(null);
  const [imageGenSuccess, setImageGenSuccess] = useState<string | null>(null);

  // Player Preview playback states
  const [previewActiveSceneIndex, setPreviewActiveSceneIndex] = useState(0);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const previewAudioRef = React.useRef<HTMLAudioElement | null>(null);

  // Final Render states
  const [isRenderingFinal, setIsRenderingFinal] = useState(false);
  const [renderProgress, setRenderProgress] = useState<{ phase: string; percent: number }>({ phase: '', percent: 0 });
  const [finalVideoUrl, setFinalVideoUrl] = useState<string | null>(null);
  const [finalRenderError, setFinalRenderError] = useState<string | null>(null);
  const [playerViewMode, setPlayerViewMode] = useState<'final' | 'preview'>('preview');

  // Quality Render Settings
  const [renderQualityPreset, setRenderQualityPreset] = useState<'1080p' | '720p' | '480p_draft'>('1080p');
  const [renderFps, setRenderFps] = useState<number>(30);
  const [renderBitrateCrf, setRenderBitrateCrf] = useState<number>(23);

  // Video & Motion Prompt States
  const [defaultVisualMode, setDefaultVisualMode] = useState<'image' | 'video'>('image');
  const [selectedVideoProvider, setSelectedVideoProvider] = useState<string>('comfyui');
  const [selectedVideoModels, setSelectedVideoModels] = useState<Record<'t2v' | 'i2v', string | null>>({ t2v: null, i2v: null });
  const [localVideoModels, setLocalVideoModels] = useState<LocalVideoModel[]>([]);
  const [generatingVideoSceneId, setGeneratingVideoSceneId] = useState<string | null>(null);
  const [suggestingMotionSceneId, setSuggestingMotionSceneId] = useState<string | null>(null);
  const [activeI2VSceneId, setActiveI2VSceneId] = useState<string | null>(null);
  const [sceneVideoPaths, setSceneVideoPaths] = useState<Record<string, SceneVideoPath>>({});
  const [sceneImageModes, setSceneImageModes] = useState<Record<string, SceneImageMode>>({});
  const [motionPromptInputs, setMotionPromptInputs] = useState<Record<string, string>>({});
  const [activeRefSceneId, setActiveRefSceneId] = useState<string | null>(null);
  const [imageAttachmentTarget, setImageAttachmentTarget] = useState<'visual' | 'reference'>('visual');
  const [updatingVisualSceneId, setUpdatingVisualSceneId] = useState<string | null>(null);
  const [uploadingRefSceneId, setUploadingRefSceneId] = useState<string | null>(null);
  const [bindingReferenceSceneId, setBindingReferenceSceneId] = useState<string | null>(null);
  const selectedJobIdRef = React.useRef<string | null>(null);

  // Step locking and progress helpers
  const hasScript = sceneRevisions.length > 0;
  const hasAllImages = hasScript && sceneRevisions.every((r) => Boolean(r.imageUrl) || Boolean(r.videoUrl));
  const imagesCount = sceneRevisions.filter((r) => Boolean(r.imageUrl) || Boolean(r.videoUrl)).length;
  const hasAllAudios = hasScript && sceneRevisions.every((r) => Boolean(r.audioUrl));
  const audiosCount = sceneRevisions.filter((r) => Boolean(r.audioUrl)).length;
  const allApproved = hasScript && sceneRevisions.every((r) => approvedSceneIds.has(r.id));
  const approvedCount = sceneRevisions.filter((r) => approvedSceneIds.has(r.id)).length;

  // 3-Step Lifecycle:
  // Step 1: แต่งบท & ตรวจอนุมัติบท
  // Step 2: สร้างภาพและเสียง (ล็อคถ้ายังไม่มีบท หรือยังไม่อนุมัติบทครบ)
  // Step 3: เรนเดอร์ Final (ล็อคถ้ายังสร้างภาพและเสียงไม่ครบ หรือยังไม่อนุมัติ)
  const isStep2Locked = !hasScript || !scriptApproved;
  const isStep3Locked = !hasScript || !scriptApproved || !hasAllImages || !hasAllAudios;

  // Form states for New Job
  const [title, setTitle] = useState('');
  const [pipelineMode, setPipelineMode] = useState<'scene_composition' | 'provider_full_video'>('scene_composition');
  const [CONTENT_FLOWS, setContentPresets] = useState(() => DEFAULT_CONTENT_FLOWS.map((p) => ({ ...p, prompt: p.desc, version: 0 })));
  const [STYLE_PRESETS, setStylePresets] = useState(() => DEFAULT_STYLE_PRESETS.map((p) => ({ ...p, version: 0 })));
  const [contentPrompt, setContentPrompt] = useState('');
  const [presetLoadError, setPresetLoadError] = useState('');
  useEffect(() => {
    apiFetch('/api/prompt-presets').then((r) => r.json()).then(({ presets }) => {
      const merge = (defaults: any[], kind: string) => {
        const items = new Map(defaults.map((p) => [p.id, p]));
        for (const row of presets || []) if (row.kind === kind) {
          if (row.deleted) items.delete(row.id);
          else items.set(row.id, { ...items.get(row.id), ...row.data, id: row.id, version: row.version });
        }
        return [...items.values()];
      };
      setContentPresets((old) => merge(old, 'content'));
      setStylePresets((old) => merge(old, 'style'));
      const savedDefault = (presets || []).find((p: any) => p.kind === 'style' && p.id === DEFAULT_STYLE_PRESETS[1].id);
      if (savedDefault?.deleted) { setSelectedStyle(''); setStylePrompt(''); }
      else if (savedDefault) setStylePrompt((old) => old === DEFAULT_STYLE_PRESETS[1].prompt ? savedDefault.data.prompt : old);
    }).catch((error) => setPresetLoadError(`โหลด Preset ไม่สำเร็จ: ${error.message}`));
  }, []);
  const saveContentPreset = (preset: EditablePreset) => {
    setContentPresets((old) => [...old.filter((p) => p.id !== preset.id), preset as any]);
    setSelectedContentFlow(preset.id);
    setContentPrompt(preset.prompt);
    setFlow(preset.category === 'commerce' ? 'commerce' : 'content');
  };
  const saveStylePreset = (preset: EditablePreset) => {
    setStylePresets((old) => [...old.filter((p) => p.id !== preset.id), preset as any]);
    setSelectedStyle(preset.id); setStylePrompt(preset.prompt);
  };
  const [selectedContentFlow, setSelectedContentFlow] = useState<string>('');
  const [flow, setFlow] = useState<'content' | 'commerce'>('content');
  const [language, setLanguage] = useState('th');
  const [aspectRatio, setAspectRatio] = useState('9:16');
  const [targetDurationSec, setTargetDurationSec] = useState<number>(30);
  const [selectedStyle, setSelectedStyle] = useState('comic_line_art');
  const [stylePrompt, setStylePrompt] = useState(STYLE_PRESETS[1].prompt);

  const handleStyleSelect = (styleId: string) => {
    setSelectedStyle(styleId);
    const found = STYLE_PRESETS.find((s) => s.id === styleId);
    if (found && found.prompt) {
      setStylePrompt(found.prompt);
    }
  };

  const handleSelectContentFlow = (flowId: string) => {
    setSelectedContentFlow(flowId);
    const flowItem = CONTENT_FLOWS.find((f) => f.id === flowId);
    if (flowItem) {
      setFlow(flowItem.category);
      setContentPrompt(flowItem.prompt);
      if (flowItem.defaultAudience) setAudience(flowItem.defaultAudience);
      if (flowItem.defaultObjective) setObjective(flowItem.defaultObjective);
      if (flowItem.defaultCta) setCta(flowItem.defaultCta);
    }
  };

  const [topic, setTopic] = useState('');
  const [audience, setAudience] = useState('คนทั่วไป');
  const [objective, setObjective] = useState('สร้างความรู้และให้สาระ');
  const [cta, setCta] = useState('กดติดตามเพื่อไม่พลาดคลิปใหม่');
  const [productName, setProductName] = useState('');
  const [productUrl, setProductUrl] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ name: string; assetId: string; url?: string }>>([]);
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  // Settings states
  const [localMediaUrl, setLocalMediaUrl] = useState(() => {
    return localStorage.getItem('g_forge_local_media_url') || 'http://localhost:3000';
  });
  const [localMediaToken, setLocalMediaToken] = useState('');
  const [localMediaHealth, setLocalMediaHealth] = useState<string | null>(null);
  const [isSavingLocalMedia, setIsSavingLocalMedia] = useState(false);
  const [localMediaSaveStatus, setLocalMediaSaveStatus] = useState<string | null>(null);
  const [connections, setConnections] = useState<any[]>([]);
  const hasGeminiConnected = connections.some((c) => c.adapterCode === 'gemini' && c.enabled);
  const hasFluxConnected = connections.some((c) => c.adapterCode === 'flux' && c.enabled);
  const hasLocalMediaConnected = connections.some((c) => c.adapterCode === 'local-media' && c.enabled);

  // Provider key form states
  const [providerType, setProviderType] = useState('gemini');
  const [providerAlias, setProviderAlias] = useState('Google Gemini');
  const [providerBaseUrl, setProviderBaseUrl] = useState('https://generativelanguage.googleapis.com');
  const [providerSecretKey, setProviderSecretKey] = useState('');
  const [providerSaveStatus, setProviderSaveStatus] = useState<string | null>(null);

  const handleProviderTypeChange = (type: string) => {
    setProviderType(type);
    if (type === 'gemini') {
      setProviderAlias('Google Gemini');
      setProviderBaseUrl('https://generativelanguage.googleapis.com');
    } else if (type === 'flux') {
      setProviderAlias('Together AI FLUX');
      setProviderBaseUrl('https://api.together.xyz');
    } else if (type === 'kling') {
      setProviderAlias('Kling AI');
      setProviderBaseUrl('https://api.klingai.com');
    } else {
      setProviderAlias('Custom Provider');
      setProviderBaseUrl('https://api.example.com');
    }
  };

  const handleSaveProviderKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!providerSecretKey) return;
    try {
      await createProviderConnection({
        alias: providerAlias,
        adapterCode: providerType,
        baseUrl: providerBaseUrl,
        secretKey: providerSecretKey,
        config: { model: 'default' },
      });
      setProviderSaveStatus(`บันทึกและเข้ารหัสกุญแจสำหรับ "${providerAlias}" สำเร็จแล้ว!`);
      setProviderSecretKey('');
      await loadConnections();
      setTimeout(() => setProviderSaveStatus(null), 5000);
    } catch (err: any) {
      alert(`บันทึกไม่สำเร็จ: ${err.message}`);
    }
  };

  useEffect(() => {
    loadJobs();
    loadConnections();
  }, []);

  // Auto-refresh jobs list whenever navigating to the 'jobs' tab
  useEffect(() => {
    if (currentTab === 'jobs') {
      loadJobs();
    }
  }, [currentTab]);

  const loadJobs = async () => {
    try {
      const data = await listJobs();
      if (data?.jobs) {
        setJobs(data.jobs);
      }
    } catch {
      // Mock fallback for decoupled preview
    }
  };

  const clearLocalCatalog = () => {
    setDynamicLocalModels([]);
    setDynamicLocalImageModels([]);
    setLocalVideoModels([]);
    setLocalVoiceCatalog({});
    setSelectedOllamaModel('');
    setSelectedLocalImageModel('');
    setSelectedVideoModels({ t2v: '', i2v: '' });
    setSelectedVoice('');
    setIsComfyOnline(false);
  };

  const checkLocalMedia = async (targetUrl?: string, targetToken?: string) => {
    // Saved credentials stay on the backend; no browser token is needed after reload.
    if (targetToken === undefined && !localMediaToken.trim()) {
      try {
        const response = await apiFetch('/api/local-media/status');
        const data = await response.json();
        setLocalMediaHealth(data.status === 'ok' ? 'Online (Healthy)'
          : data.status === 'unauthorized' ? 'Token ไม่ถูกต้อง (Unauthorized)'
          : 'Offline / Tunnel Unreachable');
        if (data.status === 'ok') {
          await refreshLocalCatalog();
        } else {
          clearLocalCatalog();
        }
      } catch {
        setLocalMediaHealth('Offline / Backend Unreachable');
        clearLocalCatalog();
      }
      return;
    }
    const rawUrl = targetUrl || localMediaUrl;
    const cleanUrl = (rawUrl || '').trim().replace(/\/+$/, '');
    const token = targetToken !== undefined ? targetToken : localMediaToken;
    try {
      // 1. Check network / tunnel health
      const healthRes = await fetch(`${cleanUrl}/health`);
      if (!healthRes.ok) {
        setLocalMediaHealth(`Offline (HTTP ${healthRes.status})`);
        clearLocalCatalog();
        return;
      }
      const healthData = await healthRes.json();
      if (healthData?.status !== 'healthy' && healthData?.status !== 'ok') {
        setLocalMediaHealth(`Offline (${healthData?.status || 'Unknown status'})`);
        clearLocalCatalog();
        return;
      }

      // 2. Validate token against /capabilities (which enforces Bearer auth)
      const authHeaders: Record<string, string> = {};
      if (token && token.trim()) {
        authHeaders['Authorization'] = `Bearer ${token.trim()}`;
      }
      const capRes = await fetch(`${cleanUrl}/capabilities`, { headers: authHeaders });
      if (capRes.status === 401) {
        setLocalMediaHealth('Token ไม่ถูกต้อง (401 Unauthorized)');
        clearLocalCatalog();
        return;
      }
      if (!capRes.ok) {
        setLocalMediaHealth(`Offline (HTTP ${capRes.status})`);
        clearLocalCatalog();
        return;
      }

      setLocalMediaHealth('Online (Healthy)');
    } catch {
      setLocalMediaHealth('Offline / Tunnel Unreachable');
      clearLocalCatalog();
    }
  };

  const fetchDynamicLocalModels = async () => {
    setIsLoadingLocalModels(true);
    try {
      const data = await getLocalMediaModels();
      const models = data.status === 'ok' && Array.isArray(data.models)
        ? (data.models as DynamicLocalModel[]).filter((m) => m.id !== 'gemini-pro-web')
        : [];
      setDynamicLocalModels(models);
      setSelectedOllamaModel((current) => reconcileSelection(current, models, (model) => model.id));
    } catch {
      setDynamicLocalModels([]);
      setSelectedOllamaModel('');
    } finally {
      setIsLoadingLocalModels(false);
    }
  };

  const fetchDynamicLocalImageModels = async (refreshBrowser = false) => {
    setIsLoadingLocalImageModels(true);
    if (refreshBrowser) setImageGenError(null);
    try {
      const data = await getLocalMediaImageModels(refreshBrowser ? selectedImageProvider : undefined);
      const models = data.online && Array.isArray(data.models) ? usableImageModels(data.models as LocalImageModel[]) : [];
      setIsComfyOnline(data.online);
      setDynamicLocalImageModels(models);
    } catch (error) {
      if (refreshBrowser) {
        setImageGenError(`รีเฟรชโมเดลภาพไม่สำเร็จ: ${error instanceof Error ? error.message : String(error)}`);
        return;
      }
      setIsComfyOnline(false);
      setDynamicLocalImageModels([]);
      setSelectedLocalImageModel('');
    } finally {
      setIsLoadingLocalImageModels(false);
    }
  };

  const fetchDynamicLocalVideoModels = async (refreshBrowser = false) => {
    setIsLoadingLocalVideoModels(true);
    if (refreshBrowser) setImageGenError(null);
    try {
      const data = await getLocalMediaVideoModels(refreshBrowser ? selectedVideoProvider : undefined);
      const models = data.online && Array.isArray(data.models) ? usableVideoModels(data.models) : [];
      setLocalVideoModels(models);
    } catch (error) {
      if (refreshBrowser) {
        setImageGenError(`รีเฟรชโมเดลวิดีโอไม่สำเร็จ: ${error instanceof Error ? error.message : String(error)}`);
        return;
      }
      setLocalVideoModels([]);
      setSelectedVideoModels({ t2v: '', i2v: '' });
    } finally {
      setIsLoadingLocalVideoModels(false);
    }
  };

  useEffect(() => {
    setSelectedLocalImageModel(current => reconcileSelection(current, usableImageModels(dynamicLocalImageModels, selectedImageProvider), model => model.id));
  }, [selectedImageProvider, dynamicLocalImageModels]);

  useEffect(() => {
    const available = usableVideoModels(localVideoModels, selectedVideoProvider);
    setSelectedVideoModels(current => ({
      t2v: reconcileSelection(current.t2v, videoModelsForMode(available, 't2v'), model => model.name),
      i2v: reconcileSelection(current.i2v, videoModelsForMode(available, 'i2v'), model => model.name),
    }));
  }, [selectedVideoProvider, localVideoModels]);

  const fetchDynamicLocalVoices = async () => {
    setIsLoadingLocalVoices(true);
    try {
      const data = await getLocalMediaAudioVoices();
      const providers = data.status === 'ok' && data.providers ? data.providers : {};
      setLocalVoiceCatalog(providers);
      const voices = providers[selectedAudioProvider] || [];
      setSelectedVoice((current) => reconcileSelection(current, voices, (voice) => voice.id));
    } catch {
      setLocalVoiceCatalog({});
      setSelectedVoice('');
    } finally {
      setIsLoadingLocalVoices(false);
    }
  };

  const refreshLocalCatalog = async () => {
    await Promise.all([fetchDynamicLocalModels(), fetchDynamicLocalImageModels(), fetchDynamicLocalVideoModels(), fetchDynamicLocalVoices()]);
  };

  const handleSaveLocalMedia = async () => {
    setIsSavingLocalMedia(true);
    setLocalMediaSaveStatus(null);
    const cleanUrl = localMediaUrl.trim().replace(/\/+$/, '');
    localStorage.setItem('g_forge_local_media_url', cleanUrl);

    try {
      await createProviderConnection({
        alias: 'Local Media Service',
        adapterCode: 'local-media',
        baseUrl: cleanUrl,
        secretKey: localMediaToken,
        config: {},
      });
      setLocalMediaSaveStatus('บันทึกการตั้งค่า Local Media Service สำเร็จแล้ว');
      setLocalMediaToken(''); // Do not retain raw secret token in browser memory
      await loadConnections();
      await checkLocalMedia(cleanUrl, localMediaToken);
      await refreshLocalCatalog();
      setTimeout(() => setLocalMediaSaveStatus(null), 4000);
    } catch (err: any) {
      alert(`บันทึกไม่สำเร็จ: ${err.message}`);
    } finally {
      setIsSavingLocalMedia(false);
    }
  };

  const loadConnections = async () => {
    try {
      const data = await listProviderConnections();
      if (data?.connections) {
        setConnections(data.connections);
        const localConn = data.connections.find((c: any) => c.adapterCode === 'local-media' && c.enabled);
        if (localConn?.baseUrl) {
          setLocalMediaUrl(localConn.baseUrl);
          localStorage.setItem('g_forge_local_media_url', localConn.baseUrl);
          checkLocalMedia(localConn.baseUrl);
          return;
        }
      }
      setLocalMediaHealth(null);
      clearLocalCatalog();
    } catch {
      // Ignore if offline
      setLocalMediaHealth(null);
      clearLocalCatalog();
    }
  };

  const handleDeleteConnection = async (id: string, alias: string) => {
    if (!window.confirm(`ต้องการลบการเชื่อมต่อ "${alias}" หรือไม่?`)) return;
    try {
      await deleteProviderConnection(id);
      await loadConnections();
    } catch (err: any) {
      alert(`ลบไม่สำเร็จ: ${err.message}`);
    }
  };

  const processSceneData = (sceneData: any) => {
    const loadedScenes = (sceneData?.scenes || [])
      .slice()
      .sort((a: any, b: any) => Number(a.orderIndex ?? 0) - Number(b.orderIndex ?? 0));
    const orderMap = new Map<string, number>(
      loadedScenes.map((s: any) => [String(s.id), Number(s.orderIndex ?? 0)])
    );
    const currentIds = new Set<string>(loadedScenes.map((s: any) => String(s.currentRevisionId)).filter((id: string) => id && id !== 'undefined'));
    const sortedRevs = (sceneData?.revisions || [])
      .filter((r: any) => currentIds.has(String(r.id)))
      .sort((a: any, b: any) => {
        const orderA = orderMap.get(String(a.sceneId)) ?? 999;
        const orderB = orderMap.get(String(b.sceneId)) ?? 999;
        if (orderA !== orderB) return orderA - orderB;
        return Number(a.revision ?? 0) - Number(b.revision ?? 0);
      });
    const approvedIds = new Set<string>();
    for (const r of sortedRevs) {
      if (r.isApproved) {
        approvedIds.add(String(r.id));
      }
    }
    return { scenes: loadedScenes, revisions: sortedRevs, approvedIds };
  };

  const navigateTo = (tab: 'jobs' | 'new_job' | 'review' | 'settings', jobId?: string | null, replace = false) => {
    let path = '/jobs';
    if (tab === 'new_job') {
      path = '/jobs/new';
    } else if (tab === 'settings') {
      path = '/settings';
    } else if (tab === 'review') {
      const targetId = jobId || selectedJobId;
      if (targetId) {
        path = `/jobs/${targetId}`;
      } else {
        path = '/jobs';
        tab = 'jobs';
      }
    }

    if (window.location.pathname !== path) {
      if (replace) {
        window.history.replaceState({ tab, jobId }, '', path);
      } else {
        window.history.pushState({ tab, jobId }, '', path);
      }
    }
    setCurrentTab(tab);
    if (tab === 'jobs') {
      loadJobs();
    }
  };

  const handleOpenReview = async (jobId: string, updateUrl = true) => {
    selectedJobIdRef.current = jobId;
    setSelectedJobId(jobId);
    if (updateUrl) {
      navigateTo('review', jobId);
    }
    setScriptGenError(null);
    setScriptGenSuccess(null);
    try {
      const detail = await getJob(jobId);
      setSelectedJobDetail(detail);
      setDefaultVisualMode(detail?.spec?.spec?.defaultVisualMode === 'video' ? 'video' : 'image');
      setScriptApproved(Boolean(detail?.scriptApproved));
      setPreviewSnapshotId(detail?.contentSnapshotId || null);
      setPreviewOutputId(detail?.previewOutputId || null);
      setApprovedSceneIds(new Set(detail?.approvedSceneRevisionIds || []));
      setPreviewVideoUrl(detail?.previewVideoUrl || null);
      if (detail?.latestVideoUrl) {
        setFinalVideoUrl(detail.latestVideoUrl);
        setPlayerViewMode('final');
      } else {
        setFinalVideoUrl(null);
        setPlayerViewMode('preview');
      }
      const sceneData = await listScenes(jobId);
      const { scenes: sortedScenes, revisions: sortedRevs, approvedIds } = processSceneData(sceneData);
      setScenes(sortedScenes);
      setSceneRevisions(sortedRevs);

      try {
        const assetsRes = await listAssets(jobId);
        if (assetsRes?.assets && Array.isArray(assetsRes.assets)) {
          setUploadedFiles(
            assetsRes.assets
              .filter((a) => a.validationStatus === 'ready')
              .map((a) => ({
                name: a.objectKey.split('/').pop() || 'Asset',
                assetId: a.id,
                url: `${BASE_URL}/api/r2/${a.objectKey.split('/').map(encodeURIComponent).join('/')}`,
              }))
          );
        }
      } catch {
        // ignore asset list errors
      }

      const provs = detail?.providers || detail?.spec?.spec?.providers;
      if (provs) {
        if (provs.script?.provider) setSelectedScriptProvider(provs.script.provider);
        if (provs.script?.model) {
          if (provs.script.provider === 'google') {
            setSelectedGeminiModel(provs.script.model);
          } else {
            setSelectedOllamaModel(provs.script.model);
          }
        }
        if (provs.image?.provider) setSelectedImageProvider(provs.image.provider);
        if (provs.image?.model) {
          if (provs.image.provider === 'together') {
            setSelectedFluxModel(provs.image.model);
          } else {
            setSelectedLocalImageModel(provs.image.model);
          }
        }
        if (provs.audio?.provider) setSelectedAudioProvider(provs.audio.provider);
        if (provs.audio?.voice) setSelectedVoice(provs.audio.voice);
      }
    } catch (err: any) {
      console.error('Failed to load job details:', err);
      setScenes([]);
      setSceneRevisions([]);
      setApprovedSceneIds(new Set());
      setScriptApproved(false);
      setPreviewSnapshotId(null);
      setPreviewOutputId(null);
      setPreviewVideoUrl(null);
    }
    setCurrentTab('review');
  };

  // Sync route with browser URL on mount and popstate (back/forward)
  useEffect(() => {
    const syncFromURL = () => {
      const path = window.location.pathname;
      if (path === '/jobs/new') {
        setCurrentTab('new_job');
      } else if (path === '/settings') {
        setCurrentTab('settings');
      } else if (path.startsWith('/jobs/')) {
        const id = path.replace('/jobs/', '').split('/')[0];
        if (id && id !== 'new') {
          handleOpenReview(id, false);
        } else {
          setCurrentTab('jobs');
          loadJobs();
        }
      } else {
        setCurrentTab('jobs');
        loadJobs();
      }
    };

    syncFromURL();
    window.addEventListener('popstate', syncFromURL);
    return () => window.removeEventListener('popstate', syncFromURL);
  }, []);

  const handleDeleteJob = async (jobId: string, jobTitle: string) => {
    if (!window.confirm(`ต้องการลบงาน "${jobTitle}" หรือไม่?\nข้อมูลทั้งหมดรวมถึงฉากและสคริปต์จะถูกลบถาวร`)) {
      return;
    }
    try {
      await deleteJob(jobId);
      if (selectedJobId === jobId) {
        selectedJobIdRef.current = null;
        setSelectedJobId(null);
        setSelectedJobDetail(null);
        setScenes([]);
        setSceneRevisions([]);
        setApprovedSceneIds(new Set());
        navigateTo('jobs');
      }
      await loadJobs();
    } catch (err: any) {
      alert(`ลบงานไม่สำเร็จ: ${err.message}`);
    }
  };

  // Helper to fallback image loading to local backend R2 proxy if media.g-forge.site has no local DNS
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.currentTarget;
    if (target.src.includes('media.g-forge.site')) {
      try {
        const urlObj = new URL(target.src);
        const key = urlObj.pathname.replace(/^\//, '');
        target.src = `http://localhost:4100/api/r2/${key}`;
      } catch {
        // keep current src
      }
    }
  };

  const handleTriggerGenerateScript = async (jobId: string, customModel?: string, customProvider?: string) => {
    setIsGeneratingScript(true);
    setScriptGenError(null);
    setScriptGenSuccess(null);
    try {
      const providerToUse = customProvider || selectedScriptProvider;
      let modelToUse = customModel;
      if (!modelToUse) {
        if (providerToUse === 'ollama') {
          modelToUse = selectedOllamaModel || undefined;
        } else if (providerToUse === 'google_web') {
          modelToUse = 'gemini-pro-web';
        } else {
          modelToUse = selectedGeminiModel;
        }
      }
      if (providerToUse === 'ollama' && (!modelToUse || !dynamicLocalModels.some((model) => model.id === modelToUse))) {
        throw new Error('กรุณาเลือก Local LLM ที่ Local Media รายงานว่าพร้อมใช้งาน');
      }
      if (providerToUse === 'google_web' && (!hasLocalMediaConnected || !localMediaHealth?.includes('Online'))) {
        throw new Error('กรุณาเชื่อมต่อ Local Media Service ก่อนใช้งาน Google Gemini Pro (Browser)');
      }
      const providerName = SCRIPT_PROVIDERS.find((p) => p.id === providerToUse)?.name || providerToUse;
      const res = await generateScript(jobId, modelToUse, providerToUse);
      if (res?.status === 'success') {
        setScriptGenSuccess(res.message || `✨ ${providerName} (${modelToUse}) แต่งบทสำเร็จ ${res.scenesCount || 0} ฉากเรียบร้อยแล้ว`);
        const detail = await getJob(jobId);
        setSelectedJobDetail(detail);
        loadJobs().catch(() => {});
        setScriptApproved(false);
        setPreviewSnapshotId(null);
        setPreviewOutputId(null);
        setPreviewVideoUrl(null);
        const sceneData = await listScenes(jobId);
        const { scenes: sortedScenes, revisions: sortedRevs, approvedIds } = processSceneData(sceneData);
        setScenes(sortedScenes);
        setSceneRevisions(sortedRevs);
        setApprovedSceneIds(approvedIds);
      } else {
        const errorMsg = res?.error || res?.message || `${providerName} ไม่ได้ตอบกลับมา หรือเกิดข้อผิดพลาดในการประมวลผล`;
        setScriptGenError(errorMsg);
      }
    } catch (err: any) {
      setScriptGenError(err.message || 'ไม่สามารถเชื่อมต่อระบบเพื่อแต่งบทได้');
    } finally {
      setIsGeneratingScript(false);
    }
  };

  const handleCreateJobSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedContentFlow) {
      alert('กรุณาเลือกประเภทเนื้อหา (Content Flow) ในขั้นตอนที่ 1 ก่อนสร้างงาน!');
      return;
    }
    if (!contentPrompt.trim() || !Number.isInteger(targetDurationSec) || targetDurationSec <= 0) {
      alert('กรุณากรอก Content Prompt และระยะเวลาที่เป็นจำนวนเต็มบวก');
      return;
    }
    setIsCreatingJob(true);
    setScriptGenError(null);
    setScriptGenSuccess(null);
    try {
      const isCommerce = selectedContentFlow.startsWith('commerce') || flow === 'commerce';
      if (isCommerce && uploadedFiles.length === 0) {
        if (!confirm('คุณยังไม่ได้อัปโหลดรูปภาพสินค้าสำหรับงานประเภท Commerce ต้องการดำเนินการต่อโดยไม่มีรูปภาพสินค้าหรือไม่?')) {
          setIsCreatingJob(false);
          return;
        }
      }

      const res = await createJob({
        title,
        pipelineMode,
        flow,
        contentFlow: selectedContentFlow,
        contentPrompt,
        aspectRatio,
        targetDurationSec,
        style: selectedStyle,
        stylePrompt,
        defaultVisualMode,
        topic,
        audience,
        objective,
        language,
        cta,
        productName: isCommerce ? productName : undefined,
        productUrl: isCommerce ? productUrl : undefined,
        assetIds: isCommerce ? uploadedFiles.map((f) => f.assetId) : [],
      });
      if (res?.jobId) {
        await loadJobs();

        if (res.scriptStatus === 'success') {
          setScriptGenSuccess(res.scriptMessage || `✨ Google Gemini แต่งบทสำเร็จ ${res.scenesCount || 0} ฉากเรียบร้อยแล้ว`);
        } else if (res.scriptStatus === 'failed') {
          setScriptGenError(`สร้างงานสำเร็จ แต่ Google Gemini แต่งบทไม่สำเร็จ: ${res.scriptError}`);
        }

        await handleOpenReview(res.jobId);
      }
    } catch (err: any) {
      alert(`สร้างงานไม่สำเร็จ: ${err.message}`);
    } finally {
      setIsCreatingJob(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingFile(true);
    try {
      const completeRes = await uploadAsset(file);

      setUploadedFiles((prev) => [
        ...prev,
        { name: file.name, assetId: completeRes.assetId },
      ]);
    } catch (err: any) {
      alert(`อัปโหลดไฟล์ไม่สำเร็จ: ${err.message}`);
    } finally {
      setIsUploadingFile(false);
      e.target.value = '';
    }
  };

  const handleToggleSceneApproval = async (sceneId: string, sceneRevId: string) => {
    if (!selectedJobId) return;
    try {
      const res = await toggleSceneApproval(selectedJobId, sceneId);
      if (res?.status === 'success') {
        setApprovedSceneIds((prev) => {
          const next = new Set(prev);
          if (res.isApproved) {
            next.add(sceneRevId);
          } else {
            next.delete(sceneRevId);
          }
          return next;
        });
      }
    } catch (err: any) {
      alert(`ไม่สามารถเปลี่ยนสถานะการอนุมัติได้: ${err.message}`);
    }
  };

  const handleApproveAll = async () => {
    if (!selectedJobId) return;
    try {
      let scriptRevId = selectedJobDetail?.job?.currentScriptRevisionId || selectedJobDetail?.project?.currentScriptRevisionId;
      if (!scriptRevId) {
        const freshDetail = await getJob(selectedJobId);
        setSelectedJobDetail(freshDetail);
        scriptRevId = freshDetail?.job?.currentScriptRevisionId || freshDetail?.project?.currentScriptRevisionId;
      }
      if (!scriptRevId) throw new Error('ไม่พบ script revision ปัจจุบัน');
      await decideScript(selectedJobId, scriptRevId, 'approve');
      setScriptApproved(true);
      const updated = await getJob(selectedJobId);
      setSelectedJobDetail(updated);
    } catch (err: any) {
      alert(`ไม่สามารถอนุมัติบทได้: ${err.message}`);
    }
  };

  const handleApproveScenes = async () => {
    if (!selectedJobId) return;
    try {
      const res = await approveAllScenes(selectedJobId);
      if (res?.status === 'success') {
        const allIds = new Set(sceneRevisions.map((r) => r.id));
        setApprovedSceneIds(allIds);
      }
    } catch (err: any) {
      alert(`ไม่สามารถอนุมัติทุกฉากได้: ${err.message}`);
    }
  };

  // Automated slideshow playback and audio sync when preview is playing
  useEffect(() => {
    if (!isPreviewPlaying || sceneRevisions.length === 0) {
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
        previewAudioRef.current.onended = null;
      }
      return;
    }

    const advanceToNext = () => {
      setPreviewActiveSceneIndex((prev) => {
        if (prev + 1 >= sceneRevisions.length) {
          setIsPreviewPlaying(false);
          return 0;
        }
        return prev + 1;
      });
    };

    let timer: ReturnType<typeof setTimeout> | null = null;
    const currentAudioUrl = sceneRevisions[previewActiveSceneIndex]?.audioUrl;

    if (currentAudioUrl) {
      if (!previewAudioRef.current) {
        previewAudioRef.current = new Audio(currentAudioUrl);
      } else {
        previewAudioRef.current.src = currentAudioUrl;
        previewAudioRef.current.load();
      }

      // Transition strictly after audio ends naturally (+ 400ms pause)
      previewAudioRef.current.onended = () => {
        timer = setTimeout(advanceToNext, 400);
      };

      previewAudioRef.current.play().catch(() => {
        // Fallback if browser blocks autoplay or fails to load audio
        const fallbackDuration = sceneRevisions[previewActiveSceneIndex]?.timingDurationMs || 4000;
        timer = setTimeout(advanceToNext, fallbackDuration);
      });
    } else {
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
        previewAudioRef.current.onended = null;
      }
      // No audio for this scene, use timingDurationMs or 4s fallback
      const duration = sceneRevisions[previewActiveSceneIndex]?.timingDurationMs || 4000;
      timer = setTimeout(advanceToNext, duration);
    }

    return () => {
      if (timer) clearTimeout(timer);
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
        previewAudioRef.current.onended = null;
      }
    };
  }, [isPreviewPlaying, previewActiveSceneIndex, sceneRevisions]);

  const handleGenerateSceneAudio = async (sceneId: string, customVoice?: string, customProvider?: string) => {
    if (!selectedJobId) return;
    setGeneratingAudioSceneId(sceneId);
    setScriptGenError(null);
    try {
      const providerToUse = customProvider || selectedAudioProvider;
      const voiceToUse = customVoice || selectedVoice;
      if (!voiceToUse || !localVoiceCatalog[providerToUse]?.some((voice) => voice.id === voiceToUse)) {
        throw new Error('กรุณาเลือกเสียงที่ Local Media รายงานว่าพร้อมใช้งาน');
      }
      const res = await generateSceneAudio(selectedJobId, sceneId, voiceToUse, providerToUse);
      if (res?.status === 'success' && res.audioUrl) {
        setScriptGenSuccess('✨ สร้างเสียงพากย์สำเร็จเรียบร้อยแล้ว');
        const sceneData = await listScenes(selectedJobId);
        const { scenes: sortedScenes, revisions: sortedRevs, approvedIds } = processSceneData(sceneData);
        setScenes(sortedScenes);
        setSceneRevisions(sortedRevs);
        setApprovedSceneIds(approvedIds);
        getJob(selectedJobId).then(setSelectedJobDetail).catch(() => {});
        loadJobs().catch(() => {});
      } else {
        setScriptGenError(res?.error || res?.message || 'ไม่สามารถสร้างเสียงได้');
      }
    } catch (err: any) {
      setScriptGenError(`เกิดข้อผิดพลาดในการสร้างเสียง: ${err.message}`);
    } finally {
      setGeneratingAudioSceneId(null);
    }
  };

  const handleGenerateAllAudios = async (customVoice?: string, customProvider?: string) => {
    if (!selectedJobId) return;
    setIsGeneratingAllAudios(true);
    setScriptGenError(null);
    setScriptGenSuccess(null);
    try {
      const providerToUse = customProvider || selectedAudioProvider;
      const voiceToUse = customVoice || selectedVoice;
      if (!voiceToUse || !localVoiceCatalog[providerToUse]?.some((voice) => voice.id === voiceToUse)) {
        throw new Error('กรุณาเลือกเสียงที่ Local Media รายงานว่าพร้อมใช้งาน');
      }
      const res = await generateAllAudios(selectedJobId, voiceToUse, providerToUse);
      if (res?.status === 'success') {
        const provName = AUDIO_PROVIDERS.find((p) => p.id === providerToUse)?.name || providerToUse;
        setScriptGenSuccess(`✨ ${res.message || `สร้างเสียงพากย์ครบทั้งหมด ${res.count || sceneRevisions.length} ฉากด้วย ${provName} สำเร็จแล้ว`}`);
        const sceneData = await listScenes(selectedJobId);
        const { scenes: sortedScenes, revisions: sortedRevs, approvedIds } = processSceneData(sceneData);
        setScenes(sortedScenes);
        setSceneRevisions(sortedRevs);
        setApprovedSceneIds(approvedIds);
        getJob(selectedJobId).then(setSelectedJobDetail).catch(() => {});
        loadJobs().catch(() => {});
      } else {
        setScriptGenError(res?.error || res?.message || 'สร้างเสียงพากย์ไม่สำเร็จ');
      }
    } catch (err: any) {
      setScriptGenError(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setIsGeneratingAllAudios(false);
    }
  };

  const handleGenerateSceneImage = async (sceneId: string, customModel?: string, customProvider?: string) => {
    if (!selectedJobId || bindingReferenceSceneId === sceneId) return;
    const scene = sceneRevisions.find((revision) => revision.sceneId === sceneId);
    if (!scene) return;
    const mode: SceneImageMode = scene.referenceImageUrl ? (sceneImageModes[sceneId] || 'i2i') : 't2i';
    setGeneratingImageSceneId(sceneId);
    setImageGenError(null);
    setImageGenSuccess(null);
    try {
      const providerToUse = customProvider || selectedImageProvider;
      if (mode === 'i2i' && !['local_sd', 'google_flow', 'gemini_web'].includes(providerToUse)) {
        throw new Error('ผู้ให้บริการนี้ยังไม่รองรับภาพอ้างอิง');
      }
      let modelToUse = customModel;
      if (!modelToUse) {
        if (providerToUse === 'together') {
          modelToUse = selectedFluxModel;
        } else if (providerToUse === 'google_flow' || providerToUse === 'gemini_web') {
          modelToUse = selectedLocalImageModel || undefined;
        } else {
          modelToUse = selectedLocalImageModel || undefined;
        }
      }
      if ((providerToUse === 'google_flow' || providerToUse === 'gemini_web') && (!hasLocalMediaConnected || !localMediaHealth?.includes('Online'))) {
        throw new Error(`กรุณาเชื่อมต่อ Local Media Service ก่อนสร้างภาพด้วย ${providerToUse === 'google_flow' ? 'Google Flow' : 'Gemini Pro'}`);
      }
      if (providerToUse === 'local_sd' && (!modelToUse || !usableImageModels(dynamicLocalImageModels, providerToUse).some((model) => model.id === modelToUse))) {
        throw new Error('กรุณาเลือก Image Checkpoint ที่ Local Media รายงานว่าพร้อมใช้งาน');
      }
      const res = await generateSceneImage(selectedJobId, sceneId, modelToUse, providerToUse, mode);
      if (res?.status === 'success' && res.imageUrl) {
        setImageGenSuccess(`✨ สร้างภาพสำเร็จและบันทึกเข้า Cloudflare R2 เรียบร้อยแล้ว`);
        const sceneData = await listScenes(selectedJobId);
        const { scenes: sortedScenes, revisions: sortedRevs, approvedIds } = processSceneData(sceneData);
        setScenes(sortedScenes);
        setSceneRevisions(sortedRevs);
        setApprovedSceneIds(approvedIds);
        getJob(selectedJobId).then(setSelectedJobDetail).catch(() => {});
        loadJobs().catch(() => {});
      } else {
        setImageGenError(res?.error || res?.message || 'ไม่สามารถสร้างภาพได้');
      }
    } catch (err: any) {
      setImageGenError(`เกิดข้อผิดพลาดในการสร้างภาพ: ${err.message}`);
    } finally {
      setGeneratingImageSceneId(null);
    }
  };

  const handleGenerateAllImages = async (customModel?: string, customProvider?: string) => {
    if (!selectedJobId || bindingReferenceSceneId !== null) return;
    const requestJobId = selectedJobId;
    setIsGeneratingAllImages(true);
    setImageGenError(null);
    setImageGenSuccess(null);
    try {
      const providerToUse = customProvider || selectedImageProvider;
      let modelToUse = customModel;
      if (!modelToUse) {
        if (providerToUse === 'together') {
          modelToUse = selectedFluxModel;
        } else if (providerToUse === 'google_flow' || providerToUse === 'gemini_web') {
          modelToUse = selectedLocalImageModel || undefined;
        } else {
          modelToUse = selectedLocalImageModel || undefined;
        }
      }
      if ((providerToUse === 'google_flow' || providerToUse === 'gemini_web') && (!hasLocalMediaConnected || !localMediaHealth?.includes('Online'))) {
        throw new Error(`กรุณาเชื่อมต่อ Local Media Service ก่อนสร้างภาพด้วย ${providerToUse === 'google_flow' ? 'Google Flow' : 'Gemini Pro'}`);
      }
      if (providerToUse === 'local_sd' && (!modelToUse || !usableImageModels(dynamicLocalImageModels, providerToUse).some((model) => model.id === modelToUse))) {
        throw new Error('กรุณาเลือก Image Checkpoint ที่ Local Media รายงานว่าพร้อมใช้งาน');
      }
      let completed = 0;
      const failed: string[] = [];
      for (const [index, scene] of sceneRevisions.entries()) {
        const mode: SceneImageMode = scene.referenceImageUrl ? (sceneImageModes[scene.sceneId] || 'i2i') : 't2i';
        if (mode === 'i2i' && !['local_sd', 'google_flow', 'gemini_web'].includes(providerToUse)) {
          failed.push(`ฉาก ${index + 1}: ผู้ให้บริการนี้ยังไม่รองรับภาพอ้างอิง`);
          continue;
        }
        try {
          const result = await generateSceneImage(requestJobId, scene.sceneId, modelToUse, providerToUse, mode);
          if (result.status !== 'success' || !result.imageUrl) throw new Error(result.message || result.error || 'ไม่มีภาพจากผู้ให้บริการ');
          completed += 1;
        } catch (error) {
          failed.push(`ฉาก ${index + 1}: ${error instanceof Error ? error.message : String(error)}`);
        }
      }
      if (completed > 0) {
        const sceneData = await listScenes(requestJobId);
        const { scenes: sortedScenes, revisions: sortedRevs, approvedIds } = processSceneData(sceneData);
        if (selectedJobIdRef.current === requestJobId) {
          setScenes(sortedScenes);
          setSceneRevisions(sortedRevs);
          setApprovedSceneIds(approvedIds);
          setPreviewActiveSceneIndex(0);
          getJob(requestJobId).then(setSelectedJobDetail).catch(() => {});
          loadJobs().catch(() => {});
        }
      }
      if (selectedJobIdRef.current === requestJobId) {
        if (completed > 0) setImageGenSuccess(`สร้างภาพสำเร็จ ${completed}/${sceneRevisions.length} ฉาก`);
        if (failed.length > 0) setImageGenError(failed.join('\n'));
      }
    } catch (err: any) {
      setImageGenError(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setIsGeneratingAllImages(false);
    }
  };

  const handleDispatchPreview = async () => {
    if (!selectedJobId) return;
    if (!hasAllImages || !hasAllAudios) {
      alert('กรุณาสร้างภาพและเสียงให้ครบทุกฉากก่อนเรนเดอร์ Preview');
      return;
    }
    setIsRenderingPreview(true);
    setPreviewOutputId(null);
    setPreviewVideoUrl(null);
    setApprovedSceneIds(new Set());
    try {
      const result = await dispatchRender(selectedJobId, 'preview', '', { crf: 23, fps: 30 });
      if (!result.renderJobId || !result.contentSnapshotId) throw new Error('ไม่พบ Render Job หรือ Content Snapshot');
      setPreviewSnapshotId(result.contentSnapshotId);
      const completed = await waitForRender(result.renderJobId);
      if (!completed.outputId || !completed.videoUrl) throw new Error('Preview ไม่ได้บันทึก output ที่ตรวจสอบแล้ว');
      setPreviewOutputId(completed.outputId);
      setPreviewVideoUrl(completed.videoUrl);
      setPlayerViewMode('preview');
    } catch (err: any) {
      setFinalRenderError(`Preview render error: ${err.message}`);
    } finally {
      setIsRenderingPreview(false);
    }
  };

  const waitForRender = async (renderJobId: string) => {
    for (let attempt = 0; attempt < 600; attempt++) {
      const result = await pollRender(renderJobId);
      if (result.status === 'succeeded') return result;
      if (result.status === 'failed') throw new Error(result.renderJob?.errorMessage || 'การเรนเดอร์ล้มเหลว');
      setRenderProgress({ phase: result.progressPhase || result.status, percent: result.progressPercent || 0 });
      await new Promise((resolve) => setTimeout(resolve, 1500));
    }
    throw new Error('รอผลเรนเดอร์นานเกินไป กรุณาเปิดงานนี้ใหม่เพื่อตรวจสถานะ');
  };

  const handleDispatchFinal = async () => {
    if (!selectedJobId) return;

    if (sceneRevisions.length === 0) {
      alert('ยังไม่มีฉากสำหรับงานนี้ กรุณาแต่งบทและสร้างฉากก่อน');
      return;
    }

    if (!allApproved) {
      alert(`ระบบล็อก Final Render: กรุณาอนุมัติฉากให้ครบทุกฉากก่อนเรนเดอร์งานจริง (อนุมัติแล้ว ${approvedSceneIds.size}/${sceneRevisions.length} ฉาก)`);
      return;
    }

    const missingVisuals = sceneRevisions.some((r) => !r.imageUrl && !r.videoUrl);
    if (missingVisuals) {
      alert('มีบางฉากยังไม่มีภาพหรือวิดีโอ กรุณาสร้างภาพหรือวิดีโอให้ครบทุกฉากก่อนเรนเดอร์งานจริง');
      return;
    }

    const missingAudios = sceneRevisions.some((r) => !r.audioUrl);
    if (missingAudios) {
      alert('มีบางฉากยังไม่มีเสียงพากย์ กรุณาสร้างเสียงให้ครบทุกฉากก่อนเรนเดอร์งานจริง');
      return;
    }

    setIsRenderingFinal(true);
    setFinalRenderError(null);
    setFinalVideoUrl(null);
    setRenderProgress({ phase: 'queued', percent: 0 });

    try {
      const res = await dispatchRender(selectedJobId, 'final', '', {
        qualityPreset: renderQualityPreset,
        fps: renderFps,
        bitrateCrf: renderBitrateCrf,
      });
      if (res?.error) {
        setFinalRenderError(res.error.message || 'เกิดข้อผิดพลาดในการส่งคำสั่งเรนเดอร์');
        setIsRenderingFinal(false);
        return;
      }

      const renderJobId = res.renderJobId;
      if (!renderJobId) {
        setFinalRenderError('ไม่พบ Render Job ID จากระบบ');
        setIsRenderingFinal(false);
        return;
      }

      // Start polling for Remotion live progress from Local Media Service
      const pollInterval = setInterval(async () => {
        try {
          const pollRes = await pollRender(renderJobId);
          if (pollRes?.status === 'succeeded' && pollRes.videoUrl) {
            clearInterval(pollInterval);
            setIsRenderingFinal(false);
            setRenderProgress({ phase: 'succeeded', percent: 100 });
            setFinalVideoUrl(pollRes.videoUrl);
            setPlayerViewMode('final');
          } else if (pollRes?.status === 'failed') {
            clearInterval(pollInterval);
            setIsRenderingFinal(false);
            setFinalRenderError(pollRes.renderJob?.errorMessage || 'การเรนเดอร์ด้วย Remotion ล้มเหลว');
          } else if (pollRes?.status === 'running') {
            setRenderProgress({
              phase: pollRes.progressPhase || 'rendering',
              percent: pollRes.progressPercent || 10,
            });
          }
        } catch (err: any) {
          console.warn('Poll render error:', err);
        }
      }, 1500);

    } catch (err: any) {
      setFinalRenderError(`Final render error: ${err.message}`);
      setIsRenderingFinal(false);
    }
  };

  const handleSuggestMotionPrompt = async (sceneId: string) => {
    if (!selectedJobId) return;
    setSuggestingMotionSceneId(sceneId);
    try {
      const res = await suggestMotionPrompt(selectedJobId, sceneId);
      if (res && res.motionPrompt) {
        setMotionPromptInputs((prev) => ({ ...prev, [sceneId]: res.motionPrompt }));
      }
    } catch (err: any) {
      alert(`ไม่สามารถขอคำแนะนำ Motion Prompt ได้: ${err.message}`);
    } finally {
      setSuggestingMotionSceneId(null);
    }
  };

  const videoPathForScene = (scene: SceneRevision): SceneVideoPath => {
    const requested = sceneVideoPaths[scene.sceneId];
    if (requested === 'generated' && scene.imageUrl) return requested;
    if (requested === 'reference' && scene.referenceImageUrl) return requested;
    if (requested === 't2v') return requested;
    return scene.imageUrl ? 'generated' : scene.referenceImageUrl ? 'reference' : 't2v';
  };

  const handleGenerateSceneVideo = async (sceneId: string) => {
    if (!selectedJobId) return;
    const requestJobId = selectedJobId;
    const scene = sceneRevisions.find((revision) => revision.sceneId === sceneId || revision.id === sceneId);
    if (!scene || bindingReferenceSceneId === scene.sceneId) return;
    const requestRevisionId = scene.id;
    const path = videoPathForScene(scene);
    const mode = path === 't2v' ? 't2v' : 'i2v';
    const isBrowserProvider = selectedVideoProvider === 'google_flow' || selectedVideoProvider === 'gemini_web';
    let modelToUse: string | undefined = selectedVideoModels[mode] || undefined;

    if (!isBrowserProvider) {
      if (selectedVideoProvider !== 'comfyui') {
        alert(`ผู้ให้บริการวิดีโอ ${selectedVideoProvider} ยังไม่พร้อมใช้งาน`);
        return;
      }
      const compatibleModel = videoModelsForMode(usableVideoModels(localVideoModels, selectedVideoProvider), mode).find((model) => model.name === selectedVideoModels[mode]);
      if (!compatibleModel) {
        alert(`ไม่มีโมเดลวิดีโอที่เลือกสำหรับโหมด ${mode.toUpperCase()} กรุณารีเฟรชและเลือกโมเดลใน Step 2`);
        return;
      }
      modelToUse = compatibleModel.name;
    }

    setGeneratingVideoSceneId(sceneId);
    try {
      const prompt = motionPromptInputs[sceneId] ?? sceneRevisions.find((revision) => revision.sceneId === sceneId || revision.id === sceneId)?.motionPrompt ?? '';
      const res = await generateSceneVideo(requestJobId, sceneId, {
        mode,
        imageSource: path === 't2v' ? undefined : path,
        motionPrompt: prompt,
        provider: selectedVideoProvider,
        model: modelToUse,
      });
      setSceneRevisions((prev) =>
        selectedJobIdRef.current !== requestJobId ? prev : prev.map((revision) =>
          (revision.sceneId === sceneId || revision.id === sceneId) && revision.id === requestRevisionId
            ? { ...revision, ...res.revision, videoUrl: res.videoUrl || revision.videoUrl }
            : revision
        )
      );
      setActiveI2VSceneId(null);
    } catch (err: any) {
      alert(`สร้างวิดีโอล้มเหลว: ${err.message}`);
    } finally {
      setGeneratingVideoSceneId(null);
    }
  };

  const applySceneReference = (jobId: string, sceneId: string, sceneRevisionId: string, imageUrl: string, assetId?: string) => {
    if (selectedJobIdRef.current !== jobId) return;
    setSceneRevisions((prev) => prev.map((revision) => revision.id === sceneRevisionId && (revision.sceneId === sceneId || revision.id === sceneId)
      ? { ...revision, referenceImageUrl: imageUrl, referenceAssetId: assetId }
      : revision));
    setSceneImageModes((prev) => ({ ...prev, [sceneId]: assetId ? 'i2i' : 't2i' }));
    setActiveRefSceneId(null);
  };

  const handleSetReferenceImage = async (sceneId: string, imageUrl: string, assetId?: string): Promise<boolean> => {
    if (!selectedJobId || bindingReferenceSceneId !== null) return false;
    const jobId = selectedJobId;
    if (imageUrl && !assetId) {
      alert('เลือกภาพที่อัปโหลดและตรวจสอบแล้วก่อนใช้เป็นภาพอ้างอิง');
      return false;
    }
    try {
      // The server resolves the canonical image from its validated asset ID;
      // imageUrl may be a frontend preview proxy when choosing a project asset.
      const result = await setSceneReferenceImage(jobId, sceneId, assetId ? { referenceAssetId: assetId } : { referenceImageUrl: '' });
      applySceneReference(jobId, sceneId, result.sceneRevisionId, result.referenceImageUrl ?? imageUrl, result.referenceAssetId ?? assetId);
      return true;
    } catch (err: any) {
      alert(`แนบภาพอ้างอิงไม่สำเร็จ: ${err.message}`);
      return false;
    }
  };

  const handleReuseSceneReferenceImage = async (sceneId: string, sourceSceneId: string) => {
    if (!selectedJobId || bindingReferenceSceneId !== null || isGeneratingAllImages) return;
    const jobId = selectedJobId;
    setBindingReferenceSceneId(sceneId);
    try {
      const result = await setSceneReferenceImage(jobId, sceneId, { sourceSceneId });
      if (!result.referenceAssetId || !result.referenceImageUrl) {
        throw new Error('เซิร์ฟเวอร์ไม่ได้คืนภาพอ้างอิงที่ตรวจสอบแล้ว');
      }
      applySceneReference(jobId, sceneId, result.sceneRevisionId, result.referenceImageUrl, result.referenceAssetId);
    } catch (err: any) {
      alert(`แนบภาพอ้างอิงไม่สำเร็จ: ${err.message}`);
    } finally {
      setBindingReferenceSceneId(null);
    }
  };

  const handleChangeSceneVisualImage = async (sceneId: string, action: 'clear' | 'attach', assetId?: string): Promise<boolean> => {
    if (!selectedJobId || bindingReferenceSceneId === sceneId || (action === 'attach' && !assetId)) return false;
    const jobId = selectedJobId;
    setUpdatingVisualSceneId(sceneId);
    try {
      const result = await setSceneVisualImage(jobId, sceneId, action === 'clear' ? { action } : { action, assetId: assetId! });
      if (selectedJobIdRef.current === jobId) {
        setSceneRevisions((prev) => prev.map((revision) =>
          revision.id === result.sceneRevisionId
            ? { ...revision, imageUrl: result.imageUrl || undefined, referenceImageUrl: undefined, referenceAssetId: undefined }
            : revision
        ));
        setSceneImageModes((prev) => ({ ...prev, [sceneId]: 't2i' }));
        setActiveRefSceneId(null);
      }
      return true;
    } catch (err: any) {
      alert(`${action === 'clear' ? 'ลบภาพฉาก' : 'แนบภาพฉาก'}ไม่สำเร็จ: ${err.message}`);
      return false;
    } finally {
      setUpdatingVisualSceneId(null);
    }
  };

  const handleUploadSceneImage = async (sceneId: string, file: File, target: 'visual' | 'reference') => {
    if (!file || !selectedJobId || bindingReferenceSceneId !== null) return;
    setUploadingRefSceneId(sceneId);
    try {
      const completeRes = await uploadAsset(file, selectedJobId);

      const publicUrl = completeRes.publicUrl || `${BASE_URL}/api/r2/${String(completeRes.objectKey).split('/').map(encodeURIComponent).join('/')}`;
      if (target === 'visual') {
        if (!await handleChangeSceneVisualImage(sceneId, 'attach', completeRes.assetId)) return;
      } else {
        if (!await handleSetReferenceImage(sceneId, publicUrl, completeRes.assetId)) return;
      }
      setUploadedFiles((prev) => {
        if (prev.some((f) => f.assetId === completeRes.assetId)) return prev;
        return [...prev, { name: file.name, assetId: completeRes.assetId, url: publicUrl }];
      });
    } catch (err: any) {
      alert(`อัปโหลดภาพไม่สำเร็จ: ${err.message}`);
    } finally {
      setUploadingRefSceneId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 flex flex-col font-sans">
      {/* Top Navigation */}
      <header className="border-b border-slate-800 bg-slate-950/80 backdrop-blur sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div
            onClick={() => navigateTo('jobs')}
            className="flex items-center gap-3 cursor-pointer select-none group"
            title="กลับไปหน้ารายการงาน"
          >
            <div className="w-10 h-10 rounded-lg bg-sky-500/20 border border-sky-400/30 flex items-center justify-center text-sky-400 group-hover:scale-105 transition">
              <Film className="w-6 h-6" />
            </div>
            <div>
              <div className="text-lg font-bold tracking-tight text-white flex items-center gap-2 group-hover:text-sky-300 transition">
                G-Forge Media
              </div>
              <p className="text-xs text-slate-400">Multi-Platform Scene Review & Automation</p>
            </div>
          </div>

          <nav className="flex items-center gap-2">
            <button
              onClick={() => navigateTo('jobs')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-2 transition ${
                currentTab === 'jobs'
                  ? 'bg-sky-500 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              <Layers className="w-4 h-4" /> รายการงาน
            </button>
            {selectedJobId && (
              <button
                onClick={() => navigateTo('review', selectedJobId)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-2 transition ${
                  currentTab === 'review'
                    ? 'bg-sky-500 text-white'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
              >
                <Video className="w-4 h-4" /> ตรวจฉาก
              </button>
            )}
            <button
              onClick={() => navigateTo('settings')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-2 transition ${
                currentTab === 'settings'
                  ? 'bg-sky-500 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              <Settings className="w-4 h-4" /> ตั้งค่าระบบ
            </button>
          </nav>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 py-8">
        {/* TAB 1: JOBS DASHBOARD */}
        {currentTab === 'jobs' && (
          <JobsDashboard
            jobs={jobs}
            navigateTo={navigateTo}
            handleDeleteJob={handleDeleteJob}
            handleOpenReview={handleOpenReview}
          />
        )}

        {/* TAB 2: NEW JOB WIZARD */}
        {currentTab === 'new_job' && (
          <div className="max-w-3xl mx-auto space-y-6">
            <div>
              <h1 className="text-2xl font-bold text-white">สร้างงานผลิตสื่อใหม่</h1>
              <p className="text-sm text-slate-400">กำหนดรูปแบบแพลตฟอร์ม โหมดไปป์ไลน์ และเนื้อหาเริ่มต้น</p>
            </div>

            <form onSubmit={handleCreateJobSubmit} className="space-y-6 bg-slate-800/40 border border-slate-700/60 rounded-2xl p-6">
              {/* Pipeline Mode Toggle */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">โหมดไปป์ไลน์</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setPipelineMode('scene_composition')}
                    className={`p-4 rounded-xl border text-left transition ${
                      pipelineMode === 'scene_composition'
                        ? 'bg-sky-500/10 border-sky-500 text-white'
                        : 'bg-slate-800/60 border-slate-700 text-slate-400 hover:bg-slate-800'
                    }`}
                  >
                    <div className="font-semibold text-sm text-sky-400 flex items-center gap-2">
                      <Layers className="w-4 h-4" /> Multi-Scene Composition
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      สร้างบท แยกฉาก พากย์เสียงภาษาไทย ตรวจภาพรายฉาก และประกอบด้วย Remotion
                    </p>
                  </button>

                  <button
                    type="button"
                    disabled
                    className={`p-4 rounded-xl border text-left transition ${
                      pipelineMode === 'provider_full_video'
                        ? 'bg-sky-500/10 border-sky-500 text-white'
                        : 'bg-slate-800/60 border-slate-700 text-slate-400 hover:bg-slate-800'
                    }`}
                  >
                    <div className="font-semibold text-sm text-amber-400 flex items-center gap-2">
                      <Sparkles className="w-4 h-4" /> Provider Full Video
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      ยังไม่พร้อมใช้งาน: Local Media ยังไม่รองรับงานวิดีโอทั้งคลิปจาก Provider
                    </p>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="text-sm text-slate-300">ภาษาบทและคำบรรยาย
                  <select value={language} onChange={(e) => setLanguage(e.target.value)} className="mt-2 w-full rounded-lg bg-slate-900 p-2">
                    <option value="th">ไทย</option><option value="en">English</option>
                  </select>
                </label>
              </div>
              {/* 1. Aspect Ratio Selection (Separated) */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-slate-300">
                    อัตราส่วนภาพ <span className="text-sky-400">*</span>
                  </label>
                  <span className="text-xs text-slate-400">ขนาดความละเอียดการเรนเดอร์</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {RATIO_PRESETS.map((r) => {
                    const Icon = r.icon;
                    const isSelected = aspectRatio === r.id;
                    return (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => setAspectRatio(r.id)}
                        className={`p-3 rounded-xl border text-left transition flex items-start gap-3 ${
                          isSelected
                            ? 'bg-sky-500/15 border-sky-400 text-white shadow-lg shadow-sky-500/10 ring-1 ring-sky-400/50'
                            : 'bg-slate-800/60 border-slate-700 text-slate-300 hover:bg-slate-800'
                        }`}
                      >
                        <div
                          className={`p-2 rounded-lg shrink-0 ${
                            isSelected ? 'bg-sky-500 text-white' : 'bg-slate-900 text-slate-400'
                          }`}
                        >
                          <Icon className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="font-semibold text-xs text-white">{r.label}</div>
                          <div className="text-[11px] text-sky-400 font-mono mt-0.5">{r.resolution}</div>
                          <div className="text-[10px] text-slate-400 mt-0.5">{r.desc}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Target Duration Selection */}
              <div className="pt-3 border-t border-slate-700/60">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-slate-300 flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-sky-400" />
                    ระยะเวลาคลิป <span className="text-sky-400">*</span>
                  </label>
                  <span className="text-xs text-sky-400 font-medium">
                    เป้าหมาย {targetDurationSec} วินาที {targetDurationSec >= 60 && `(${Math.floor(targetDurationSec / 60)} นาที ${targetDurationSec % 60 ? `${targetDurationSec % 60} วินาที` : ''})`}
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                  {DURATION_PRESETS.map((d) => {
                    const isSelected = targetDurationSec === d.seconds;
                    return (
                      <button
                        key={d.seconds}
                        type="button"
                        onClick={() => setTargetDurationSec(d.seconds)}
                        className={`p-3 rounded-xl border text-left transition ${
                          isSelected
                            ? 'bg-sky-500/15 border-sky-400 text-white shadow-lg shadow-sky-500/10 ring-1 ring-sky-400/50'
                            : 'bg-slate-800/60 border-slate-700 text-slate-300 hover:bg-slate-800'
                        }`}
                      >
                        <div className="font-semibold text-xs text-white">{d.label}</div>
                        <div className="text-[10px] text-slate-400 mt-0.5">{d.desc}</div>
                      </button>
                    );
                  })}
                </div>
                <div className="mt-2.5 flex items-center gap-2">
                  <span className="text-xs text-slate-400">หรือกำหนดเอง (ไม่จำกัดความยาว):</span>
                  <input
                    type="number"
                    min="1"
                    value={targetDurationSec}
                    onChange={(e) => setTargetDurationSec(Math.max(1, parseInt(e.target.value) || 1))}
                    placeholder="เช่น 120, 300..."
                    className="w-28 px-2.5 py-1 bg-slate-900 border border-slate-700 rounded-lg text-xs text-white focus:outline-none focus:border-sky-500 font-mono"
                  />
                  <span className="text-xs text-slate-400">วินาที</span>
                </div>
              </div>

              {/* ขั้นตอนที่ 1: เลือกประเภทเนื้อหา */}
              <div className="space-y-4 pt-3 border-t border-slate-700/60">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-semibold text-white flex items-center gap-2">
                      <span className="flex items-center justify-center w-5 h-5 rounded-full bg-sky-500 text-[11px] font-bold text-white">
                        1
                      </span>
                      เลือกประเภทเนื้อหา <span className="text-sky-400">*</span>
                    </label>
                    <p className="text-xs text-slate-400 mt-0.5">
                      เลือกรูปแบบการดำเนินเรื่อง ส่วนสไตล์ภาพเลือกแยกได้อย่างอิสระ
                    </p>
                  </div>
                  {selectedContentFlow && (
                    <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-medium flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" /> เลือกแล้ว: {CONTENT_FLOWS.find((f) => f.id === selectedContentFlow)?.label}
                    </span>
                  )}
                </div>

                {/* Content Category: สาระ & เล่าเรื่อง */}
                <div>
                  <div className="text-xs font-semibold text-sky-400 mb-2 flex items-center gap-1.5 uppercase tracking-wider">
                    <BookOpen className="w-3.5 h-3.5" /> ฝั่งสาระ ความรู้ และเล่าเรื่อง
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {CONTENT_FLOWS.filter((f) => f.category === 'content').map((f) => {
                      const isSelected = selectedContentFlow === f.id;
                      return (
                        <button
                          key={f.id}
                          type="button"
                          onClick={() => handleSelectContentFlow(f.id)}
                          className={`p-3.5 rounded-xl border text-left transition relative flex flex-col justify-between ${
                            isSelected
                              ? 'bg-sky-500/15 border-sky-400 ring-1 ring-sky-400/50 shadow-lg shadow-sky-500/10'
                              : 'bg-slate-850/60 border-slate-700/70 hover:bg-slate-800 hover:border-slate-600'
                          }`}
                        >
                          <div>
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <span className="font-semibold text-xs text-white">{f.label}</span>
                              <span className="text-[10px] px-2 py-0.5 rounded bg-sky-500/10 text-sky-300 border border-sky-500/20 font-medium shrink-0">
                                {f.badge}
                              </span>
                            </div>
                            <p className="text-[11px] text-slate-400 leading-relaxed line-clamp-2">{f.desc}</p>
                          </div>
                          <div className="mt-2.5 pt-2 border-t border-slate-700/40 flex items-center justify-between text-[10px] text-slate-400">
                            <span>สไตล์แนะนำ: <strong className="text-slate-300">{STYLE_PRESETS.find((s) => s.id === f.recommendedStyle)?.label?.split(' ')[0]}</strong></span>
                            {isSelected && <span className="text-emerald-400 font-semibold flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> ใช้งานอยู่</span>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Commerce Category: สินค้า & รีวิว */}
                <div>
                  <div className="text-xs font-semibold text-amber-400 mb-2 flex items-center gap-1.5 uppercase tracking-wider">
                    <ShoppingBag className="w-3.5 h-3.5" /> ฝั่งสินค้า รีวิว และการตลาด
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    {CONTENT_FLOWS.filter((f) => f.category === 'commerce').map((f) => {
                      const isSelected = selectedContentFlow === f.id;
                      return (
                        <button
                          key={f.id}
                          type="button"
                          onClick={() => handleSelectContentFlow(f.id)}
                          className={`p-3.5 rounded-xl border text-left transition relative flex flex-col justify-between ${
                            isSelected
                              ? 'bg-amber-500/15 border-amber-400 ring-1 ring-amber-400/50 shadow-lg shadow-amber-500/10'
                              : 'bg-slate-850/60 border-slate-700/70 hover:bg-slate-800 hover:border-slate-600'
                          }`}
                        >
                          <div>
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <span className="font-semibold text-xs text-white">{f.label}</span>
                              <span className="text-[10px] px-2 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20 font-medium shrink-0">
                                {f.badge}
                              </span>
                            </div>
                            <p className="text-[11px] text-slate-400 leading-relaxed line-clamp-2">{f.desc}</p>
                          </div>
                          <div className="mt-2.5 pt-2 border-t border-slate-700/40 flex items-center justify-between text-[10px] text-slate-400">
                            <span>สไตล์แนะนำ: <strong className="text-amber-300">สินค้าสมจริง</strong></span>
                            {isSelected && <span className="text-amber-400 font-semibold flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> ใช้งานอยู่</span>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {presetLoadError && <p role="alert" className="text-sm text-rose-300">{presetLoadError}</p>}
              <PromptPresetEditor kind="content" selected={CONTENT_FLOWS.find((p) => p.id === selectedContentFlow)} prompt={contentPrompt} onPromptChange={setContentPrompt} onSaved={saveContentPreset} onDeleted={(id) => { setContentPresets((old) => old.filter((p) => p.id !== id)); setSelectedContentFlow(''); }} />

              {/* ขั้นตอนที่ 2: สไตล์ภาพของวิดีโอ - ต้องเลือก Flow ก่อน */}
              <div className="pt-3 border-t border-slate-700/60">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-semibold text-white flex items-center gap-2">
                        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-sky-500 text-[11px] font-bold text-white">
                          2
                        </span>
                        <Palette className="w-4 h-4 text-sky-400" /> สไตล์ภาพของวิดีโอ
                      </label>
                      <span className="text-xs text-emerald-400 flex items-center gap-1">
                        <Unlock className="w-3.5 h-3.5" /> เลือกแยกจากประเภทเนื้อหา
                      </span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                      {STYLE_PRESETS.map((s) => {
                        const isSelected = selectedStyle === s.id;
                        const activeFlow = CONTENT_FLOWS.find((f) => f.id === selectedContentFlow);
                        const isRecommended = activeFlow?.recommendedStyle === s.id;
                        return (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => handleStyleSelect(s.id)}
                            className={`p-3 rounded-xl border text-left transition relative ${
                              isSelected
                                ? 'bg-sky-500/15 border-sky-400 text-white ring-1 ring-sky-400/50 shadow-md'
                                : 'bg-slate-800/50 border-slate-700 text-slate-300 hover:bg-slate-800'
                            }`}
                          >
                            {isRecommended && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30 font-semibold mb-1.5 inline-block">
                                ✨ แนะนำสำหรับ Flow นี้
                              </span>
                            )}
                            <div className="font-semibold text-xs text-white">{s.label}</div>
                            <div className="text-[10px] text-slate-400 mt-1 line-clamp-1">{s.desc}</div>
                          </button>
                        );
                      })}
                    </div>

                    <PromptPresetEditor kind="style" selected={STYLE_PRESETS.find((p) => p.id === selectedStyle)} prompt={stylePrompt} onPromptChange={setStylePrompt} onSaved={saveStylePreset} onDeleted={(id) => { setStylePresets((old) => old.filter((p) => p.id !== id)); setSelectedStyle(''); }} />
                  </div>
              </div>

              {/* ขั้นตอนที่ 3: กลุ่มเป้าหมายและวัตถุประสงค์ */}
              <div className="space-y-4 pt-3 border-t border-slate-700/60">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold text-white flex items-center gap-2">
                    <span className="flex items-center justify-center w-5 h-5 rounded-full bg-sky-500 text-[11px] font-bold text-white">
                      3
                    </span>
                    กลุ่มเป้าหมายและวัตถุประสงค์ <span className="text-sky-400">*</span>
                  </label>
                  <span className="text-xs text-slate-400">ช่วยให้ AI เขียนบทตรงกลุ่มเป้าหมายและจุดประสงค์</span>
                </div>

                {/* Target Audience */}
                <div className="space-y-2 bg-slate-900/50 p-4 rounded-xl border border-slate-700/60">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-slate-200 flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5 text-sky-400" /> กลุ่มเป้าหมาย
                    </label>
                    <span className="text-[11px] text-slate-400">เลือกพรีเซ็ตหรือพิมพ์กำหนดเอง</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {AUDIENCE_PRESETS.map((aud) => {
                      const isActive = audience === aud;
                      return (
                        <button
                          key={aud}
                          type="button"
                          onClick={() => setAudience(aud)}
                          className={`px-2.5 py-1 rounded-lg text-xs font-medium transition ${
                            isActive
                              ? 'bg-sky-500 text-white shadow-sm ring-1 ring-sky-300'
                              : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white border border-slate-700/60'
                          }`}
                        >
                          {aud}
                        </button>
                      );
                    })}
                  </div>
                  <input
                    type="text"
                    required
                    value={audience}
                    onChange={(e) => setAudience(e.target.value)}
                    placeholder="ระบุกลุ่มเป้าหมาย เช่น คนรักสุขภาพ, เจ้าของร้านอาหาร, คอเกมเมอร์..."
                    className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-xs text-white focus:outline-none focus:border-sky-500 placeholder-slate-500"
                  />
                </div>

                {/* Objective */}
                <div className="space-y-2 bg-slate-900/50 p-4 rounded-xl border border-slate-700/60">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-slate-200 flex items-center gap-1.5">
                      <Target className="w-3.5 h-3.5 text-sky-400" /> วัตถุประสงค์ของวิดีโอ
                    </label>
                    <span className="text-[11px] text-slate-400">เลือกพรีเซ็ตหรือพิมพ์กำหนดเอง</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {OBJECTIVE_PRESETS.map((obj) => {
                      const isActive = objective === obj;
                      return (
                        <button
                          key={obj}
                          type="button"
                          onClick={() => setObjective(obj)}
                          className={`px-2.5 py-1 rounded-lg text-xs font-medium transition ${
                            isActive
                              ? 'bg-sky-500 text-white shadow-sm ring-1 ring-sky-300'
                              : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white border border-slate-700/60'
                          }`}
                        >
                          {obj}
                        </button>
                      );
                    })}
                  </div>
                  <input
                    type="text"
                    required
                    value={objective}
                    onChange={(e) => setObjective(e.target.value)}
                    placeholder="ระบุวัตถุประสงค์ เช่น ให้คนกดเซฟคลิป, ปิดการขายสินค้าใน 60 วินาที..."
                    className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-xs text-white focus:outline-none focus:border-sky-500 placeholder-slate-500"
                  />
                </div>
              </div>

              {/* ขั้นตอนที่ 4: ข้อมูลชื่องานและเนื้อหา */}
              <div className="space-y-4 pt-3 border-t border-slate-700/60">
                <div>
                  <label className="block text-sm font-semibold text-white mb-1 flex items-center gap-2">
                    <span className="flex items-center justify-center w-5 h-5 rounded-full bg-sky-500 text-[11px] font-bold text-white">
                      4
                    </span>
                    ชื่องาน <span className="text-sky-400">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="เช่น สรุปข่าว AI ประจำสัปดาห์ หรือ รีวิวไมค์ไร้สาย"
                    className="w-full px-3.5 py-2.5 bg-slate-900 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:border-sky-500"
                  />
                </div>

                {/* Dynamic Inputs depending on flow */}
                {flow === 'content' ? (
                  <div>
                    <label className="block text-xs font-semibold text-slate-200 mb-1">
                      หัวข้อหลักและประเด็นสำคัญ <span className="text-sky-400">*</span>
                    </label>
                    <textarea
                      required
                      rows={3}
                      value={topic}
                      onChange={(e) => setTopic(e.target.value)}
                      placeholder="ระบุประเด็นสำคัญ เนื้อเรื่อง หรือโครงเรื่องที่ต้องการให้ AI แต่งบท..."
                      className="w-full px-3.5 py-2 bg-slate-900 border border-slate-700 rounded-lg text-xs text-white focus:outline-none focus:border-sky-500"
                    />
                  </div>
                ) : (
                  <div className="space-y-4 p-4 bg-slate-900/60 rounded-xl border border-slate-700/60">
                    <label className="block text-xs text-slate-300">รายละเอียดสินค้า/จุดขายที่ยืนยันแล้ว
                      <textarea value={topic} onChange={(e) => setTopic(e.target.value)} rows={3} className="mt-2 w-full rounded-lg bg-slate-900 p-2" placeholder="กรอกสเปก ราคา หรือข้อเท็จจริงที่ต้องการใช้ในบท" />
                    </label>
                    <p className="text-xs text-amber-300">ระบบยังไม่อ่านเนื้อหาจากลิงก์หรือวิเคราะห์ภาพสินค้าอัตโนมัติ กรุณากรอกรายละเอียดสำหรับสร้างบท</p>
                    <div>
                      <label className="block text-xs font-semibold text-slate-200 mb-1">
                        ชื่อสินค้า <span className="text-sky-400">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={productName}
                        onChange={(e) => setProductName(e.target.value)}
                        placeholder="เช่น ขาตั้งโทรศัพท์อลูมิเนียมพับได้"
                        className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-xs text-white focus:outline-none focus:border-sky-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-200 mb-1">
                        ลิงก์สินค้า ไม่บังคับ
                      </label>
                      <input
                        type="url"
                        value={productUrl}
                        onChange={(e) => setProductUrl(e.target.value)}
                        placeholder="https://shopee.co.th/product/..."
                        className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-xs text-white focus:outline-none focus:border-sky-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-200 mb-1">
                        อัปโหลดรูปภาพสินค้า
                      </label>
                      <div className="border border-dashed border-slate-700 rounded-lg p-4 text-center hover:border-sky-500 transition cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileUpload}
                          className="hidden"
                          id="product-upload"
                        />
                        <label htmlFor="product-upload" className="cursor-pointer block">
                          {isUploadingFile ? (
                            <>
                              <Loader2 className="w-6 h-6 text-sky-400 mx-auto mb-2 animate-spin" />
                              <span className="text-xs text-sky-400 font-medium">กำลังอัปโหลดและตรวจสอบความถูกต้อง...</span>
                            </>
                          ) : (
                            <>
                              <Upload className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                              <span className="text-xs text-sky-400 font-medium">กดเลือกรูปสินค้า</span>
                              <span className="text-xs text-slate-500 block mt-1">PNG, JPG ขนาดไม่เกิน 15MB</span>
                            </>
                          )}
                        </label>
                      </div>

                      {uploadedFiles.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {uploadedFiles.map((f, i) => (
                            <div key={i} className="text-xs text-emerald-400 flex items-center gap-1.5">
                              <CheckCircle className="w-3.5 h-3.5" /> {f.name} ตรวจสอบและบันทึกแล้ว
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* ขั้นตอนที่ 5: Call to Action */}
              <div className="space-y-2 pt-3 border-t border-slate-700/60">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold text-white flex items-center gap-2">
                    <span className="flex items-center justify-center w-5 h-5 rounded-full bg-sky-500 text-[11px] font-bold text-white">
                      5
                    </span>
                    คำเชิญชวนท้ายคลิป
                  </label>
                  <span className="text-xs text-slate-400">เลือกพรีเซ็ตหรือพิมพ์เอง</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {CTA_PRESETS.map((c) => {
                    const isActive = cta === c;
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setCta(c)}
                        className={`px-2.5 py-1 rounded-lg text-xs font-medium transition ${
                          isActive
                            ? 'bg-sky-500 text-white shadow-sm ring-1 ring-sky-300'
                            : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white border border-slate-700/60'
                        }`}
                      >
                        {c}
                      </button>
                    );
                  })}
                </div>
                <input
                  type="text"
                  value={cta}
                  onChange={(e) => setCta(e.target.value)}
                  placeholder="เช่น กดไลก์กดแชร์ หรือ สั่งซื้อได้ที่ตะกร้าด้านล่าง"
                  className="w-full px-3.5 py-2.5 bg-slate-900 border border-slate-700 rounded-lg text-xs text-white focus:outline-none focus:border-sky-500"
                />
              </div>

              <div className="pt-4 border-t border-slate-700 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => navigateTo('jobs')}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-sm text-slate-300 transition"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  disabled={isCreatingJob}
                  className="px-6 py-2 bg-sky-500 hover:bg-sky-600 disabled:opacity-60 rounded-lg text-sm font-semibold text-white transition shadow-lg shadow-sky-500/20 flex items-center gap-2"
                >
                  {isCreatingJob ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>กำลังสร้างงาน...</span>
                    </>
                  ) : (
                    <span>ยืนยันและไปขั้นตอนตรวจฉาก &rarr;</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* TAB 3: SCENE REVIEW & PLAYER */}
        {currentTab === 'review' && (
          <div className="space-y-6">
            {/* Top Bar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-950/60 p-4 rounded-2xl border border-slate-800">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigateTo('jobs')}
                    className="text-xs text-sky-400 hover:text-sky-300 font-medium flex items-center gap-1 transition"
                  >
                    <span>&larr;</span> รายการงาน
                  </button>
                  <span className="text-slate-600">&bull;</span>
                  <span className="text-[11px] px-2.5 py-0.5 rounded-full bg-sky-500/10 text-sky-400 border border-sky-500/20 font-medium">
                    {pipelineMode === 'scene_composition' ? 'โหมดประกอบหลายฉาก' : 'โหมดวิดีโอทันที'}
                  </span>
                </div>
                <h1 className="text-xl font-bold text-white">
                  {selectedJobDetail?.job?.title || 'ตรวจฉากและเสียงพากย์'}
                </h1>
              </div>

              <div className="flex items-center gap-3">
                <div className="px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-800 text-xs text-slate-300 flex items-center gap-2">
                  <span>ฉากทั้งหมด: <strong className="text-white">{sceneRevisions.length}</strong></span>
                  <span className="text-slate-600">|</span>
                  <span>อนุมัติแล้ว: <strong className="text-emerald-400">{approvedCount}</strong>/{sceneRevisions.length}</span>
                </div>
              </div>
            </div>

            {/* Active AI Providers Bar */}
            {(() => {
              const provs = selectedJobDetail?.providers || selectedJobDetail?.spec?.spec?.providers;
              if (!provs) return null;

              const showScript = Boolean(provs.script && hasScript);
              const showImage = Boolean(provs.image && imagesCount > 0);
              const showAudio = Boolean(provs.audio && audiosCount > 0);

              if (!showScript && !showImage && !showAudio) return null;

              return (
                <div className="flex flex-wrap items-center gap-2 p-3 rounded-xl bg-slate-900/60 border border-slate-800 text-xs">
                  <span className="text-[11px] text-slate-400 font-medium flex items-center gap-1.5 mr-1">
                    <Sparkles className="w-3.5 h-3.5 text-sky-400" /> AI Providers ในงานนี้:
                  </span>
                  {showScript && provs.script && (
                    <span className="px-2.5 py-1 rounded-lg bg-sky-950/40 border border-sky-800/40 text-sky-300 flex items-center gap-1.5">
                      <span className="text-slate-400">บท:</span>
                      <strong className="font-semibold text-sky-200">{provs.script.name || provs.script.model || provs.script.provider}</strong>
                    </span>
                  )}
                  {showImage && provs.image && (
                    <span className="px-2.5 py-1 rounded-lg bg-purple-950/40 border border-purple-800/40 text-purple-300 flex items-center gap-1.5">
                      <span className="text-slate-400">ภาพ:</span>
                      <strong className="font-semibold text-purple-200">{provs.image.name || provs.image.model || provs.image.provider}</strong>
                    </span>
                  )}
                  {showAudio && provs.audio && (
                    <span className="px-2.5 py-1 rounded-lg bg-emerald-950/40 border border-emerald-800/40 text-emerald-300 flex items-center gap-1.5">
                      <span className="text-slate-400">เสียง:</span>
                      <strong className="font-semibold text-emerald-200">{provs.audio.name || provs.audio.voice || provs.audio.provider}</strong>
                    </span>
                  )}
                </div>
              );
            })()}

            {/* Live Progress Banner when Rendering */}
            {isRenderingFinal && (
              <div className="p-4 rounded-xl bg-indigo-950/40 border border-indigo-500/40 text-indigo-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Loader2 className="w-5 h-5 text-indigo-400 animate-spin flex-shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-white">กำลังเรนเดอร์วิดีโอคุณภาพสูงด้วย Remotion...</p>
                    <p className="text-[11px] text-indigo-300/80">
                      สถานะ: {renderProgress.phase || 'rendering'} — ดำเนินการแล้ว {renderProgress.percent}%
                    </p>
                  </div>
                </div>
                <div className="w-full sm:w-48 bg-slate-800 rounded-full h-2.5 overflow-hidden border border-slate-700">
                  <div
                    className="bg-gradient-to-r from-sky-400 to-indigo-500 h-full transition-all duration-300"
                    style={{ width: `${Math.max(5, renderProgress.percent)}%` }}
                  />
                </div>
              </div>
            )}

            {/* Error Notification */}
            {finalRenderError && (
              <div className="p-4 rounded-xl bg-rose-950/40 border border-rose-500/40 text-rose-200 flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-rose-400 flex-shrink-0" />
                  <span>{finalRenderError}</span>
                </div>
                <button onClick={() => setFinalRenderError(null)} className="text-slate-400 hover:text-white">&times; ปิด</button>
              </div>
            )}

            {/* 3-Column Responsive Grid Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
              {/* Column 1: Vertical Stepper */}
              <div className="lg:col-span-4 xl:col-span-3 space-y-4">
                <div className="bg-slate-800/40 border border-slate-700/60 rounded-2xl p-4 space-y-4">
                  <div className="border-b border-slate-700/60 pb-3">
                    <h2 className="text-sm font-bold text-white flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-sky-400" />
                      ขั้นตอนการผลิต
                    </h2>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      ทำตามขั้นตอนตามลำดับเพื่อความสมบูรณ์ของวิดีโอ
                    </p>
                  </div>

                  <div className="space-y-3">
                    {/* STEP 1: แต่งบท & ตรวจอนุมัติบท */}
                    <div className={`p-3.5 rounded-xl border transition ${
                      hasScript
                        ? scriptApproved
                          ? 'bg-slate-900/60 border-slate-700'
                          : 'bg-amber-950/20 border-amber-500/40'
                        : 'bg-indigo-950/20 border-indigo-500/40'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold ${
                            !hasScript
                              ? 'bg-indigo-600 text-white'
                              : scriptApproved
                              ? 'bg-emerald-500 text-white'
                              : 'bg-amber-500 text-white'
                          }`}>
                            {scriptApproved ? <CheckCircle className="w-3.5 h-3.5" /> : '1'}
                          </span>
                          <span className="text-xs font-semibold text-white">ขั้นที่ 1: แต่งบท</span>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                          !hasScript
                            ? 'bg-slate-800 text-slate-500'
                            : scriptApproved
                            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                            : 'bg-amber-500/10 text-amber-300 border border-amber-500/20'
                        }`}>
                          {!hasScript
                            ? 'รอดำเนินการ'
                            : scriptApproved
                            ? 'อนุมัติบทแล้ว'
                            : 'รออนุมัติบท'}
                        </span>
                      </div>

                      {/* Provider Selector */}
                      <div className="space-y-1.5 mb-2.5">
                        <div className="flex items-center justify-between">
                          <label className="text-[11px] font-medium text-slate-300">เลือกผู้ให้บริการ</label>
                          <span className="text-[10px] text-slate-400 font-mono">
                            {SCRIPT_PROVIDERS.find((p) => p.id === selectedScriptProvider)?.badge}
                          </span>
                        </div>
                        <select
                          value={selectedScriptProvider}
                          onChange={(e) => setSelectedScriptProvider(e.target.value)}
                          disabled={isGeneratingScript}
                          className="w-full bg-slate-900 text-slate-200 text-xs rounded-lg px-2.5 py-1.5 border border-slate-700 focus:outline-none focus:border-indigo-500 cursor-pointer"
                        >
                          {SCRIPT_PROVIDERS.map((p) => (
                            <option key={p.id} value={p.id} disabled={p.disabled} className="bg-slate-900 text-white">
                              {p.name} {p.disabled ? '• เร็วๆ นี้' : `• ${p.badge}`}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Google Gemini Pro (Browser Automation) */}
                      {selectedScriptProvider === 'google_web' && (
                        <div className="space-y-2 mb-2">
                          <div className="flex items-center justify-between text-[11px] px-2 py-1 rounded bg-slate-950/60 border border-slate-800">
                            <span className="text-slate-400">สถานะ Local Service:</span>
                            <span className={`font-medium ${hasLocalMediaConnected && localMediaHealth?.includes('Online') ? 'text-emerald-400' : 'text-amber-400'}`}>
                              {hasLocalMediaConnected && localMediaHealth?.includes('Online')
                                ? 'พร้อมใช้งานในเครื่อง'
                                : localMediaHealth?.includes('Online')
                                ? 'ทดสอบผ่าน (ต้องบันทึกก่อน)'
                                : hasLocalMediaConnected
                                ? 'กำลังรอเชื่อมต่อ'
                                : 'ยังไม่ได้บันทึกการตั้งค่า'}
                            </span>
                          </div>
                          <div className="p-2.5 rounded-lg bg-indigo-500/10 border border-indigo-500/30 text-indigo-300 text-[11px] space-y-1">
                            <div className="flex items-center gap-1.5 font-semibold">
                              <Sparkles className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
                              Google Gemini Pro (Browser)
                            </div>
                            <p className="text-[10px] text-slate-300">
                              แต่งบทผ่าน Google Gemini Pro ในเบราว์เซอร์อัตโนมัติด้วยบัญชี Google ที่ล็อกอินไว้ ไม่ต้องใช้ API Key
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Google Gemini Warnings & Model selection */}
                      {selectedScriptProvider === 'google' && (
                        <>
                          {!hasGeminiConnected && (
                            <div className="mb-2 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[11px] space-y-1.5">
                              <div className="flex items-center gap-1.5 font-semibold">
                                <AlertCircle className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                                ยังไม่ได้เชื่อมต่อ Google Gemini
                              </div>
                              <p className="text-[10px] text-slate-300">
                                กรุณาเพิ่ม API Key ในหน้าตั้งค่าระบบก่อนใช้งานโมเดลแต่งบท
                              </p>
                              <button
                                type="button"
                                onClick={() => navigateTo('settings')}
                                className="px-2 py-1 bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 rounded text-[10px] font-medium transition"
                              >
                                ไปที่หน้าตั้งค่าระบบ &rarr;
                              </button>
                            </div>
                          )}

                          <div className="space-y-1.5 mb-2">
                            <label className="text-[11px] text-slate-400 block">โมเดล Google Gemini</label>
                            <select
                              value={selectedGeminiModel}
                              onChange={(e) => setSelectedGeminiModel(e.target.value)}
                              disabled={isGeneratingScript}
                              className="w-full bg-slate-900 text-slate-200 text-xs rounded-lg px-2.5 py-1.5 border border-slate-700 focus:outline-none focus:border-indigo-500 cursor-pointer"
                            >
                              {GEMINI_SCRIPT_MODELS.map((m) => (
                                <option key={m.id} value={m.id} className="bg-slate-900 text-white">
                                  {m.name} {!hasGeminiConnected ? '• ยังไม่เชื่อมต่อ' : ''}
                                </option>
                              ))}
                            </select>
                          </div>
                        </>
                      )}

                      {/* Local Media Ollama / LM Studio Model Selection */}
                      {selectedScriptProvider === 'ollama' && (
                        <div className="space-y-2 mb-2">
                          <div className="flex items-center justify-between text-[11px] px-2 py-1 rounded bg-slate-950/60 border border-slate-800">
                            <span className="text-slate-400">สถานะ Local Service:</span>
                            <span className={`font-medium ${hasLocalMediaConnected && localMediaHealth?.includes('Online') ? 'text-emerald-400' : 'text-amber-400'}`}>
                              {hasLocalMediaConnected && localMediaHealth?.includes('Online')
                                ? 'พร้อมใช้งานในเครื่อง'
                                : localMediaHealth?.includes('Online')
                                ? 'ทดสอบผ่าน (ต้องบันทึกก่อน)'
                                : hasLocalMediaConnected
                                ? 'กำลังรอเชื่อมต่อ'
                                : 'ยังไม่ได้บันทึกการตั้งค่า'}
                            </span>
                          </div>

                          <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                              <label className="text-[11px] text-slate-400 block">โมเดล Local LLM ในเครื่อง</label>
                              <button
                                type="button"
                                onClick={() => fetchDynamicLocalModels()}
                                disabled={isLoadingLocalModels}
                                title="รีเฟรชค้นหาโมเดลจริงที่กำลังรันอยู่ใน LM Studio และ Ollama"
                                className="text-[10px] text-sky-400 hover:text-sky-300 flex items-center gap-1 transition px-1.5 py-0.5 rounded hover:bg-slate-800/80"
                              >
                                <RefreshCw className={`w-2.5 h-2.5 ${isLoadingLocalModels ? 'animate-spin' : ''}`} />
                                {isLoadingLocalModels ? 'กำลังค้นหา...' : 'รีเฟรชโมเดล'}
                              </button>
                            </div>
                            <select
                              value={selectedOllamaModel ?? ''}
                              onChange={(e) => setSelectedOllamaModel(e.target.value)}
                              disabled={isGeneratingScript || isLoadingLocalModels || dynamicLocalModels.length === 0}
                              className="w-full bg-slate-900 text-slate-200 text-xs rounded-lg px-2.5 py-1.5 border border-slate-700 focus:outline-none focus:border-indigo-500 cursor-pointer font-mono"
                            >
                              <option value="" disabled>เลือกโมเดลที่ Local Media พบ</option>
                              {dynamicLocalModels.length > 0 ? (
                                dynamicLocalModels.map((m) => (
                                  <option key={m.id} value={m.id} className="bg-slate-900 text-white">
                                    {m.name} • {m.source}
                                  </option>
                                ))
                              ) : (
                                <option value="" disabled className="bg-slate-900 text-slate-400">
                                  {isLoadingLocalModels
                                    ? 'กำลังค้นหาโมเดลในเครื่อง...'
                                    : '(ไม่พบโมเดลที่เปิดอยู่ — กรุณาเปิด LM Studio หรือ Ollama)'}
                                </option>
                              )}
                            </select>
                          </div>
                        </div>
                      )}

                      <button
                        onClick={() => selectedJobId && handleTriggerGenerateScript(selectedJobId)}
                        disabled={
                          isGeneratingScript ||
                          isGeneratingAllImages ||
                          (selectedScriptProvider === 'google_web' && (!hasLocalMediaConnected || !localMediaHealth?.includes('Online'))) ||
                          (selectedScriptProvider === 'google' && !hasGeminiConnected) ||
                          (selectedScriptProvider === 'ollama' && (!hasLocalMediaConnected || !localMediaHealth?.includes('Online') || !selectedOllamaModel))
                        }
                        className="w-full py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition shadow"
                      >
                        <Sparkles className={`w-3.5 h-3.5 ${isGeneratingScript ? 'animate-spin' : ''}`} />
                        {isGeneratingScript
                          ? 'กำลังแต่งบท...'
                          : selectedScriptProvider === 'google_web' && (!hasLocalMediaConnected || !localMediaHealth?.includes('Online'))
                          ? 'Local Service ออฟไลน์'
                          : selectedScriptProvider === 'google' && !hasGeminiConnected
                          ? 'ต้องเชื่อมต่อ Gemini ก่อน'
                          : selectedScriptProvider === 'ollama' && !hasLocalMediaConnected
                          ? 'ต้องบันทึก Local Service ก่อน'
                          : selectedScriptProvider === 'ollama' && !localMediaHealth?.includes('Online')
                          ? 'Local Service ออฟไลน์'
                          : hasScript
                          ? 'แต่งบทใหม่'
                          : 'แต่งบท'}
                      </button>

                      {hasScript && (
                        <div className="pt-2.5 border-t border-slate-700/60 mt-2.5 space-y-2">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-300 font-medium">การอนุมัติบท:</span>
                            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                              scriptApproved
                                ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                                : 'bg-amber-500/10 text-amber-300 border border-amber-500/20'
                            }`}>
                              {scriptApproved ? 'อนุมัติแล้ว' : 'รออนุมัติ'}
                            </span>
                          </div>

                          {!scriptApproved ? (
                            <div className="space-y-1.5">
                              <p className="text-[11px] text-amber-300/90 leading-tight">
                                ⚠️ กรุณาตรวจสอบบทและกดอนุมัติ เพื่อปลดล็อกขั้นที่ 2 (สร้างภาพและเสียง)
                              </p>
                              <button
                                type="button"
                                onClick={handleApproveAll}
                                className="w-full py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition shadow"
                              >
                                <CheckCircle className="w-3.5 h-3.5" /> อนุมัติบท
                              </button>
                            </div>
                          ) : (
                            <div className="text-[11px] text-emerald-400 flex items-center justify-center gap-1.5 py-1.5 bg-emerald-950/30 rounded-lg border border-emerald-500/30 font-medium">
                              <CheckCircle className="w-3.5 h-3.5" /> บทผ่านการอนุมัติแล้ว ปลดล็อกขั้นที่ 2 เรียบร้อย
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* STEP 2: สร้างภาพและเสียง */}
                    <div className={`p-3.5 rounded-xl border transition ${
                      isStep2Locked
                        ? 'bg-slate-900/30 border-slate-800 opacity-60'
                        : hasAllImages && hasAllAudios
                        ? 'bg-emerald-950/20 border-emerald-500/40'
                        : 'bg-slate-900/60 border-slate-700'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold ${
                            isStep2Locked
                              ? 'bg-slate-800 text-slate-500'
                              : hasAllImages && hasAllAudios
                              ? 'bg-emerald-500 text-white'
                              : 'bg-sky-600 text-white'
                          }`}>
                            {hasAllImages && hasAllAudios ? (
                              <CheckCircle className="w-3.5 h-3.5" />
                            ) : isStep2Locked ? (
                              <Lock className="w-3 h-3" />
                            ) : (
                              '2'
                            )}
                          </span>
                          <span className="text-xs font-semibold text-white">ขั้นที่ 2: สร้างภาพและเสียง</span>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                          isStep2Locked
                            ? 'bg-slate-800 text-slate-500'
                            : hasAllImages && hasAllAudios
                            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                            : 'bg-sky-500/10 text-sky-300'
                        }`}>
                          {isStep2Locked
                            ? 'ล็อกอยู่'
                            : hasAllImages && hasAllAudios
                            ? 'เสร็จสมบูรณ์'
                            : `ภาพ ${imagesCount}/${sceneRevisions.length} • เสียง ${audiosCount}/${sceneRevisions.length}`}
                        </span>
                      </div>

                      {isStep2Locked ? (
                        <div className="space-y-1.5 mt-1">
                          <p className="text-[11px] text-slate-400 flex items-center gap-1.5">
                            <Lock className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                            {!hasScript
                              ? 'กรุณาแต่งบทในขั้นที่ 1 ก่อน'
                              : `ต้องอนุมัติบทในขั้นที่ 1 ให้ครบก่อน (${approvedCount} / ${sceneRevisions.length} ฉาก)`}
                          </p>
                          {hasScript && !scriptApproved && (
                            <button
                              type="button"
                              onClick={handleApproveAll}
                              className="w-full py-1.5 bg-emerald-600/80 hover:bg-emerald-600 text-white rounded-lg text-xs font-medium flex items-center justify-center gap-1.5 transition mt-1"
                            >
                              <CheckCircle className="w-3.5 h-3.5" /> อนุมัติบททุกฉากตอนนี้
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-3.5 mt-2.5">
                          {/* 2.0 ผู้ให้บริการวิดีโอ (Video AI) */}
                          <div className="p-2.5 rounded-xl bg-slate-950/40 border border-slate-800/80 space-y-2">
                            <div className="flex items-center justify-between pb-1 border-b border-slate-800">
                              <span className="text-xs font-semibold text-indigo-400 flex items-center gap-1.5">
                                <Film className="w-3.5 h-3.5" /> วิดีโอสั้น (Video AI)
                              </span>
                              <span className="text-[10px] text-slate-400 font-mono">
                                {VIDEO_PROVIDERS.find((p) => p.id === selectedVideoProvider)?.badge}
                              </span>
                            </div>

                            {/* Video AI Provider Config */}
                            <div className="space-y-1.5">
                              <div className="flex items-center justify-between">
                                <label className="text-[10px] text-slate-400">เลือกผู้ให้บริการวิดีโอ</label>
                              </div>
                              <select
                                value={selectedVideoProvider}
                                onChange={(e) => setSelectedVideoProvider(e.target.value)}
                                className="w-full bg-slate-900 text-slate-200 text-xs rounded-lg px-2 py-1.5 border border-slate-700 focus:outline-none focus:border-indigo-500 cursor-pointer"
                              >
                                {VIDEO_PROVIDERS.map((p) => (
                                  <option key={p.id} value={p.id} disabled={p.disabled} className="bg-slate-900 text-white">
                                    {p.name} • {p.badge}
                                  </option>
                                ))}
                              </select>
                              {selectedVideoProvider === 'google_flow' && (
                                <div className="space-y-1 pt-0.5">
                                  <div className="p-2 rounded-lg bg-indigo-500/10 border border-indigo-500/30 text-indigo-300 text-[10px] space-y-1">
                                    <div className="flex items-center gap-1.5 font-semibold">
                                      <Sparkles className="w-3 h-3 text-indigo-400 shrink-0" />
                                      Google Flow Browser
                                    </div>
                                    <p className="text-[10px] text-slate-300">
                                      ใช้โมเดลและโหมดที่ตรวจพบจากบัญชี Google Flow ที่ล็อกอินไว้
                                    </p>
                                  </div>
                                </div>
                              )}
                              {selectedVideoProvider === 'gemini_web' && (
                                <div className="space-y-1 pt-0.5">
                                  <div className="p-2 rounded-lg bg-purple-500/10 border border-purple-500/30 text-purple-300 text-[10px] space-y-1">
                                    <div className="flex items-center gap-1.5 font-semibold">
                                      <Sparkles className="w-3 h-3 text-purple-400 shrink-0" />
                                      Google Gemini Browser
                                    </div>
                                    <p className="text-[10px] text-slate-300">
                                      ใช้โมเดลและโหมดที่ตรวจพบจากบัญชี Gemini ที่ล็อกอินไว้
                                    </p>
                                  </div>
                                </div>
                              )}
                              {['comfyui', 'google_flow', 'gemini_web'].includes(selectedVideoProvider) && (
                                <div className="space-y-1 pt-0.5">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10px] text-slate-400">โมเดลวิดีโอที่ Local Media ตรวจพบ</label>
                                    <button type="button" onClick={() => fetchDynamicLocalVideoModels(true)} disabled={isLoadingLocalVideoModels}
                                      className="text-[10px] text-sky-400 hover:text-sky-300 disabled:opacity-50 flex items-center gap-1">
                                      <RefreshCw className={`w-2.5 h-2.5 ${isLoadingLocalVideoModels ? 'animate-spin' : ''}`} /> รีเฟรช
                                    </button>
                                  </div>
                                  {(['t2v', 'i2v'] as const).map((mode) => {
                                    const models = videoModelsForMode(usableVideoModels(localVideoModels, selectedVideoProvider), mode);
                                    return (
                                      <div key={mode} className="space-y-1">
                                        <label className="text-[10px] text-slate-400" htmlFor={`video-model-${mode}`}>
                                          {mode.toUpperCase()} · {mode === 't2v' ? 'ข้อความ → วิดีโอ' : 'ภาพ → วิดีโอ'}
                                        </label>
                                        <select
                                          id={`video-model-${mode}`}
                                          value={selectedVideoModels[mode] ?? ''}
                                          onChange={(e) => setSelectedVideoModels((current) => ({ ...current, [mode]: e.target.value }))}
                                          disabled={isLoadingLocalVideoModels || models.length === 0}
                                          className="w-full bg-slate-900 text-slate-200 text-xs rounded-lg px-2 py-1.5 border border-slate-700 focus:outline-none focus:border-indigo-500 cursor-pointer font-mono"
                                        >
                                          <option value="" disabled>เลือกโมเดล {mode.toUpperCase()}</option>
                                          {models.map((model) => (
                                            <option key={model.name} value={model.name} className="bg-slate-900 text-white">
                                              {model.name}
                                            </option>
                                          ))}
                                        </select>
                                        {!isLoadingLocalVideoModels && models.length === 0 && (
                                          <p className="text-[10px] text-amber-400">ยังไม่พบโมเดล {mode.toUpperCase()} ที่พร้อมใช้ใน Local Media</p>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* 2.1 ส่วนภาพประกอบ */}
                          <div className="p-2.5 rounded-xl bg-slate-950/40 border border-slate-800/80 space-y-2">
                            <div className="flex items-center justify-between pb-1 border-b border-slate-800">
                              <span className="text-xs font-semibold text-sky-400 flex items-center gap-1.5">
                                <Image className="w-3.5 h-3.5" /> ภาพประกอบ
                              </span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${
                                hasAllImages ? 'text-emerald-400 bg-emerald-950/40' : 'text-slate-400'
                              }`}>
                                {hasAllImages ? 'ครบแล้ว' : `${imagesCount} / ${sceneRevisions.length} ภาพ`}
                              </span>
                            </div>

                            {/* Image Provider */}
                            <div className="space-y-1">
                              <div className="flex items-center justify-between">
                                <label className="text-[10px] text-slate-400">เลือกผู้ให้บริการภาพ</label>
                                <span className="text-[10px] text-slate-400 font-mono">
                                  {IMAGE_PROVIDERS.find((p) => p.id === selectedImageProvider)?.badge}
                                </span>
                              </div>
                              <select
                                value={selectedImageProvider}
                                onChange={(e) => setSelectedImageProvider(e.target.value)}
                                disabled={isGeneratingAllImages}
                                className="w-full bg-slate-900 text-slate-200 text-xs rounded-lg px-2 py-1.5 border border-slate-700 focus:outline-none focus:border-sky-500 cursor-pointer"
                              >
                                {IMAGE_PROVIDERS.map((p) => (
                                  <option key={p.id} value={p.id} disabled={p.disabled} className="bg-slate-900 text-white">
                                    {p.name} {p.disabled ? '• เร็วๆ นี้' : `• ${p.badge}`}
                                  </option>
                                ))}
                              </select>
                            </div>

                            {/* Together AI FLUX Settings */}
                            {selectedImageProvider === 'together' && (
                              <>
                                {!hasFluxConnected && (
                                  <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[10px] space-y-1">
                                    <div className="flex items-center gap-1.5 font-semibold">
                                      <AlertCircle className="w-3 h-3 text-amber-400 shrink-0" />
                                      ยังไม่ได้เชื่อมต่อ Together AI FLUX
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => navigateTo('settings')}
                                      className="px-2 py-0.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 rounded text-[10px] font-medium transition"
                                    >
                                      ไปที่หน้าตั้งค่าระบบ &rarr;
                                    </button>
                                  </div>
                                )}
                                <div className="space-y-1">
                                  <label className="text-[10px] text-slate-400 block">โมเดล Together AI FLUX</label>
                                  <select
                                    value={selectedFluxModel}
                                    onChange={(e) => setSelectedFluxModel(e.target.value)}
                                    disabled={isGeneratingAllImages}
                                    className="w-full bg-slate-900 text-slate-200 text-xs rounded-lg px-2 py-1.5 border border-slate-700 focus:outline-none focus:border-sky-500 cursor-pointer"
                                  >
                                    {FLUX_IMAGE_MODELS.map((m) => (
                                      <option key={m.id} value={m.id} className="bg-slate-900 text-white">
                                        {m.name} {!hasFluxConnected ? '• ยังไม่เชื่อมต่อ' : ''}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </>
                            )}

                            {/* Local ComfyUI Settings */}
                            {['local_sd', 'google_flow', 'gemini_web'].includes(selectedImageProvider) && (
                              <div className="space-y-1.5">
                                <div className="flex items-center justify-between text-[10px] px-2 py-1 rounded bg-slate-950/60 border border-slate-800">
                                  <span className="text-slate-400">สถานะ Local Service:</span>
                                  <span className={`font-medium ${hasLocalMediaConnected && localMediaHealth?.includes('Online') ? 'text-emerald-400' : 'text-amber-400'}`}>
                                    {hasLocalMediaConnected && localMediaHealth?.includes('Online')
                                      ? 'พร้อมใช้งานในเครื่อง'
                                      : localMediaHealth?.includes('Online')
                                      ? 'ทดสอบผ่าน (ต้องบันทึกก่อน)'
                                      : hasLocalMediaConnected
                                      ? 'กำลังรอเชื่อมต่อ'
                                      : 'ยังไม่ได้บันทึกการตั้งค่า'}
                                  </span>
                                </div>

                                <div className="space-y-1">
                                  <div className="flex items-center justify-between">
                                    <label className="text-[10px] text-slate-400 block">โมเดลภาพที่ Local Media ตรวจพบ</label>
                                    <button
                                      type="button"
                                      onClick={() => fetchDynamicLocalImageModels(true)}
                                      disabled={isLoadingLocalImageModels}
                                      title="รีเฟรชโมเดลที่ Local Media ตรวจพบ"
                                      className="text-[10px] text-sky-400 hover:text-sky-300 flex items-center gap-1 transition px-1.5 py-0.5 rounded hover:bg-slate-800/80"
                                    >
                                      <RefreshCw className={`w-2.5 h-2.5 ${isLoadingLocalImageModels ? 'animate-spin' : ''}`} />
                                      {isLoadingLocalImageModels ? 'กำลังค้นหา...' : 'รีเฟรช'}
                                    </button>
                                  </div>
                                  <select
                                    value={selectedLocalImageModel ?? ''}
                                    onChange={(e) => setSelectedLocalImageModel(e.target.value)}
                                    disabled={isGeneratingAllImages || isLoadingLocalImageModels || usableImageModels(dynamicLocalImageModels, selectedImageProvider).length === 0}
                                    className="w-full bg-slate-900 text-slate-200 text-xs rounded-lg px-2 py-1.5 border border-slate-700 focus:outline-none focus:border-sky-500 cursor-pointer font-mono"
                                  >
                                    <option value="" disabled>เลือกโมเดลภาพที่ Local Media พบ</option>
                                    {usableImageModels(dynamicLocalImageModels, selectedImageProvider).length > 0 ? (
                                      usableImageModels(dynamicLocalImageModels, selectedImageProvider).map((m) => (
                                        <option key={m.id} value={m.id} className="bg-slate-900 text-white">
                                          {m.name} • {m.source || 'ComfyUI'}
                                        </option>
                                      ))
                                    ) : (
                                      <option value="" disabled className="bg-slate-900 text-slate-400">
                                        {isLoadingLocalImageModels
                                          ? 'กำลังค้นหาโมเดล...'
                                          : isComfyOnline
                                          ? '(ยังไม่พบโมเดลของผู้ให้บริการนี้)'
                                          : '(ยังไม่พบโมเดลพร้อมใช้งาน)'}
                                      </option>
                                    )}
                                  </select>
                                </div>
                              </div>
                            )}

                            <button
                              onClick={() => handleGenerateAllImages()}
                              disabled={
                                bindingReferenceSceneId !== null ||
                                isGeneratingAllImages ||
                                isStep2Locked ||
                                (selectedImageProvider === 'together' && !hasFluxConnected) ||
                                ((selectedImageProvider === 'google_flow' || selectedImageProvider === 'gemini_web') && (!hasLocalMediaConnected || !localMediaHealth?.includes('Online'))) ||
                                (selectedImageProvider === 'local_sd' && (!hasLocalMediaConnected || !localMediaHealth?.includes('Online') || !selectedLocalImageModel))
                              }
                              className="w-full py-1.5 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition shadow"
                            >
                              <Image className={`w-3.5 h-3.5 ${isGeneratingAllImages ? 'animate-spin' : ''}`} />
                              {isGeneratingAllImages
                                ? 'กำลังสร้างภาพ...'
                                : isStep2Locked
                                ? 'ต้องอนุมัติบทก่อน'
                                : selectedImageProvider === 'together' && !hasFluxConnected
                                ? 'ต้องเชื่อมต่อ Together FLUX ก่อน'
                                : (selectedImageProvider === 'google_flow' || selectedImageProvider === 'gemini_web') && (!hasLocalMediaConnected || !localMediaHealth?.includes('Online'))
                                ? 'Local Service ออฟไลน์'
                                : selectedImageProvider === 'local_sd' && !hasLocalMediaConnected
                                ? 'ต้องบันทึก Local Service ก่อน'
                                : selectedImageProvider === 'local_sd' && !localMediaHealth?.includes('Online')
                                ? 'Local Service ออฟไลน์'
                                : selectedImageProvider === 'local_sd' && !selectedLocalImageModel
                                ? 'กรุณาเลือกโมเดลภาพ'
                                : 'สร้างภาพเฉพาะฉากที่ยังไม่มี'}
                            </button>
                          </div>

                          {/* 2.2 ส่วนเสียงพากย์ */}
                          <div className="p-2.5 rounded-xl bg-slate-950/40 border border-slate-800/80 space-y-2">
                            <div className="flex items-center justify-between pb-1 border-b border-slate-800">
                              <span className="text-xs font-semibold text-violet-400 flex items-center gap-1.5">
                                <Mic className="w-3.5 h-3.5" /> เสียงพากย์
                              </span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${
                                hasAllAudios ? 'text-emerald-400 bg-emerald-950/40' : 'text-slate-400'
                              }`}>
                                {hasAllAudios ? 'ครบแล้ว' : `${audiosCount} / ${sceneRevisions.length} เสียง`}
                              </span>
                            </div>

                            {/* Audio Provider */}
                            <div className="space-y-1">
                              <div className="flex items-center justify-between">
                                <label className="text-[10px] text-slate-400">เลือกผู้ให้บริการเสียง</label>
                                <span className="text-[10px] text-slate-400 font-mono">
                                  {AUDIO_PROVIDERS.find((p) => p.id === selectedAudioProvider)?.badge}
                                </span>
                              </div>
                              <select
                                value={selectedAudioProvider}
                                onChange={(e) => {
                                  const newProvider = e.target.value;
                                  setSelectedAudioProvider(newProvider);
                                   setSelectedVoice(reconcileSelection(null, localVoiceCatalog[newProvider] || [], (voice) => voice.id));
                                }}
                                disabled={isGeneratingAllAudios}
                                className="w-full bg-slate-900 text-slate-200 text-xs rounded-lg px-2 py-1.5 border border-slate-700 focus:outline-none focus:border-violet-500 cursor-pointer"
                              >
                                {AUDIO_PROVIDERS.map((p) => (
                                  <option key={p.id} value={p.id} disabled={p.disabled} className="bg-slate-900 text-white">
                                    {p.name} {p.disabled ? '• เร็วๆ นี้' : `• ${p.badge}`}
                                  </option>
                                ))}
                              </select>
                            </div>

                            {(selectedAudioProvider === 'local_edge_tts' || selectedAudioProvider === 'local_f5_tts') && (
                              <div className="flex items-center justify-between text-[10px] px-2 py-1 rounded bg-slate-950/60 border border-slate-800">
                                <span className="text-slate-400">สถานะ Local Service:</span>
                                <span className={`font-medium ${hasLocalMediaConnected && localMediaHealth?.includes('Online') ? 'text-emerald-400' : 'text-amber-400'}`}>
                                  {hasLocalMediaConnected && localMediaHealth?.includes('Online')
                                    ? 'พร้อมใช้งานในเครื่อง'
                                    : localMediaHealth?.includes('Online')
                                    ? 'ทดสอบผ่าน (ต้องบันทึกก่อน)'
                                    : hasLocalMediaConnected
                                    ? 'กำลังรอเชื่อมต่อ'
                                    : 'ยังไม่ได้บันทึกการตั้งค่า'}
                                </span>
                              </div>
                            )}

                            {/* Voice Selector */}
                            <div className="space-y-1">
                              <div className="flex items-center justify-between">
                                <label className="text-[10px] text-slate-400 block">เลือกเสียงพากย์</label>
                                {selectedAudioProvider !== 'elevenlabs' && <button type="button" onClick={() => fetchDynamicLocalVoices()} disabled={isLoadingLocalVoices}
                                  className="text-[10px] text-sky-400 hover:text-sky-300 disabled:opacity-50 flex items-center gap-1">
                                  <RefreshCw className={`w-2.5 h-2.5 ${isLoadingLocalVoices ? 'animate-spin' : ''}`} /> รีเฟรช
                                </button>}
                              </div>
                              <select
                                value={selectedVoice ?? ''}
                                onChange={(e) => setSelectedVoice(e.target.value)}
                                disabled={isGeneratingAllAudios || (selectedAudioProvider !== 'elevenlabs' && !localVoiceCatalog[selectedAudioProvider]?.length)}
                                className="w-full bg-slate-900 text-slate-200 text-xs rounded-lg px-2 py-1.5 border border-slate-700 focus:outline-none focus:border-violet-500 cursor-pointer"
                              >
                                <option value="" disabled>เลือกเสียงที่ Local Media พบ</option>
                                {selectedAudioProvider === 'elevenlabs' ? (
                                  ELEVENLABS_VOICES.map((v) => (
                                    <option key={v.id} value={v.id} className="bg-slate-900 text-white">
                                      {v.name}
                                    </option>
                                  ))
                                ) : (
                                  (localVoiceCatalog[selectedAudioProvider] || []).map((voice) => (
                                    <option key={voice.id} value={voice.id} className="bg-slate-900 text-white">{voice.name}</option>
                                  ))
                                )}
                              </select>
                              {selectedAudioProvider !== 'elevenlabs' && !localVoiceCatalog[selectedAudioProvider]?.length &&
                                <p className="text-[10px] text-amber-400">ยังไม่พบเสียงสำหรับผู้ให้บริการนี้ใน Local Media</p>}
                            </div>

                            <button
                              onClick={() => handleGenerateAllAudios()}
                              disabled={
                                isGeneratingAllAudios ||
                                isStep2Locked ||
                                ((selectedAudioProvider === 'local_edge_tts' || selectedAudioProvider === 'local_f5_tts') && (!hasLocalMediaConnected || !localMediaHealth?.includes('Online') || !selectedVoice))
                              }
                              className="w-full py-1.5 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition shadow"
                            >
                              <Mic className={`w-3.5 h-3.5 ${isGeneratingAllAudios ? 'animate-pulse' : ''}`} />
                              {isGeneratingAllAudios
                                ? 'กำลังสร้างเสียง...'
                                : (selectedAudioProvider === 'local_edge_tts' || selectedAudioProvider === 'local_f5_tts') && !hasLocalMediaConnected
                                ? 'ต้องบันทึก Local Service ก่อน'
                                : (selectedAudioProvider === 'local_edge_tts' || selectedAudioProvider === 'local_f5_tts') && !localMediaHealth?.includes('Online')
                                ? 'Local Service ออฟไลน์'
                                : !selectedVoice
                                ? 'กรุณาเลือกเสียงพากย์'
                                : 'สร้างเสียงทุกฉาก'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* STEP 3: เรนเดอร์ Final */}
                    <div className={`p-3.5 rounded-xl border transition ${
                      isStep3Locked
                        ? 'bg-slate-900/30 border-slate-800 opacity-60'
                        : finalVideoUrl
                        ? 'bg-emerald-950/20 border-emerald-500/40'
                        : 'bg-slate-900/60 border-slate-700'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold ${
                            isStep3Locked
                              ? 'bg-slate-800 text-slate-500'
                              : finalVideoUrl
                              ? 'bg-emerald-500 text-white'
                              : 'bg-sky-600 text-white'
                          }`}>
                            {finalVideoUrl ? (
                              <CheckCircle className="w-3.5 h-3.5" />
                            ) : isStep3Locked ? (
                              <Lock className="w-3 h-3" />
                            ) : (
                              '3'
                            )}
                          </span>
                          <span className="text-xs font-semibold text-white">ขั้นที่ 3: เรนเดอร์ Final</span>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                          isStep3Locked
                            ? 'bg-slate-800 text-slate-500'
                            : finalVideoUrl
                            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                            : 'bg-sky-500/10 text-sky-300'
                        }`}>
                          {isStep3Locked ? 'ล็อกอยู่' : finalVideoUrl ? 'เสร็จสมบูรณ์' : 'พร้อมเรนเดอร์'}
                        </span>
                      </div>

                      {isStep3Locked ? (
                        <p className="text-[11px] text-slate-500 flex items-center gap-1.5 mt-1">
                          <Lock className="w-3 h-3 shrink-0" />
                          {!hasScript
                            ? 'กรุณาแต่งบทในขั้นที่ 1 ก่อน'
                             : !scriptApproved
                             ? 'ต้องอนุมัติบทในขั้นที่ 1 ก่อน'
                            : `ต้องสร้างภาพและเสียงในขั้นที่ 2 ให้ครบก่อน (ภาพ: ${imagesCount}/${sceneRevisions.length}, เสียง: ${audiosCount}/${sceneRevisions.length})`}
                        </p>
                      ) : (
                        <div className="space-y-2 mt-2">
                          {/* Provider Selector */}
                          <div className="space-y-1.5 mb-2.5">
                            <div className="flex items-center justify-between">
                              <label className="text-[11px] font-medium text-slate-300">เลือกผู้ให้บริการเรนเดอร์</label>
                              <span className="text-[10px] text-slate-400 font-mono">
                                {RENDER_PROVIDERS.find((p) => p.id === selectedRenderProvider)?.badge}
                              </span>
                            </div>
                            <select
                              value={selectedRenderProvider}
                              onChange={(e) => setSelectedRenderProvider(e.target.value)}
                              disabled={isRenderingFinal}
                              className="w-full bg-slate-900 text-slate-200 text-xs rounded-lg px-2.5 py-1.5 border border-slate-700 focus:outline-none focus:border-emerald-500 cursor-pointer"
                            >
                              {RENDER_PROVIDERS.map((p) => (
                                <option key={p.id} value={p.id} disabled={p.disabled} className="bg-slate-900 text-white">
                                  {p.name} {p.disabled ? '• เร็วๆ นี้' : `• ${p.badge}`}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="text-[11px] px-2.5 py-1.5 rounded-lg bg-slate-950/60 border border-slate-800 text-slate-400 space-y-0.5">
                            <div className="flex items-center justify-between">
                              <span>เอนจินเรนเดอร์:</span>
                              <span className="text-slate-200 font-medium">Remotion Bundled</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span>สถานะ Local Media:</span>
                              <span className={`font-medium ${hasLocalMediaConnected && localMediaHealth?.includes('Online') ? 'text-emerald-400' : 'text-amber-400'}`}>
                                {hasLocalMediaConnected && localMediaHealth?.includes('Online')
                                  ? 'พร้อมเรนเดอร์'
                                  : localMediaHealth?.includes('Online')
                                  ? 'ทดสอบผ่าน (ต้องบันทึกก่อน)'
                                  : hasLocalMediaConnected
                                  ? 'กำลังรอเชื่อมต่อ'
                                  : 'ยังไม่ได้บันทึกการตั้งค่า'}
                              </span>
                            </div>
                          </div>

                          {/* Quality Preset Selector */}
                          <div className="space-y-1">
                            <label className="text-[11px] font-medium text-slate-300">ความละเอียดและโหมดเรนเดอร์</label>
                            <select
                              value={renderQualityPreset}
                              onChange={(e) => setRenderQualityPreset(e.target.value as any)}
                              disabled={isRenderingFinal}
                              className="w-full bg-slate-900 text-slate-200 text-xs rounded-lg px-2.5 py-1.5 border border-slate-700 focus:outline-none focus:border-emerald-500 cursor-pointer"
                            >
                              {VIDEO_QUALITY_PRESETS.map((q) => (
                                <option key={q.id} value={q.id} className="bg-slate-900 text-white">
                                  {q.name}
                                </option>
                              ))}
                            </select>
                            <p className="text-[10px] text-slate-400">
                              {VIDEO_QUALITY_PRESETS.find((q) => q.id === renderQualityPreset)?.desc}
                            </p>
                          </div>

                          {/* FPS and CRF Grid */}
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <label className="text-[10px] text-slate-400">Frame Rate</label>
                              <select
                                value={renderFps}
                                onChange={(e) => setRenderFps(Number(e.target.value))}
                                disabled={isRenderingFinal}
                                className="w-full bg-slate-900 text-slate-200 text-xs rounded-lg px-2 py-1.5 border border-slate-700 focus:outline-none focus:border-emerald-500 cursor-pointer"
                              >
                                {VIDEO_FPS_OPTIONS.map((f) => (
                                  <option key={f.id} value={f.id} className="bg-slate-900 text-white">
                                    {f.id} fps
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="space-y-1">
                              <label className="text-[10px] text-slate-400">คุณภาพ Bitrate</label>
                              <select
                                value={renderBitrateCrf}
                                onChange={(e) => setRenderBitrateCrf(Number(e.target.value))}
                                disabled={isRenderingFinal}
                                className="w-full bg-slate-900 text-slate-200 text-xs rounded-lg px-2 py-1.5 border border-slate-700 focus:outline-none focus:border-emerald-500 cursor-pointer"
                              >
                                {VIDEO_BITRATE_PRESETS.map((b) => (
                                  <option key={b.id} value={b.id} className="bg-slate-900 text-white">
                                    CRF {b.id}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <button
                            onClick={handleDispatchFinal}
                            disabled={isRenderingFinal || !allApproved || isStep3Locked || !hasLocalMediaConnected || !localMediaHealth?.includes('Online')}
                            className="w-full py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 disabled:opacity-50 rounded-lg text-xs font-semibold text-white flex items-center justify-center gap-1.5 transition shadow mt-1"
                          >
                            {isRenderingFinal ? (
                              <>
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                กำลังเรนเดอร์ {renderProgress.percent}%
                              </>
                            ) : !hasLocalMediaConnected ? (
                              'ต้องบันทึก Local Media ก่อน'
                            ) : !localMediaHealth?.includes('Online') ? (
                              'Local Media ออฟไลน์'
                            ) : !allApproved ? (
                              `ต้องอนุมัติฉากให้ครบก่อน (${approvedCount}/${sceneRevisions.length})`
                            ) : renderQualityPreset === '480p_draft' ? (
                              <>
                                <Play className="w-3.5 h-3.5" />
                                สั่งเรนเดอร์ Fast Preview (480p)
                              </>
                            ) : (
                              <>
                                <ShieldCheck className="w-3.5 h-3.5" />
                                {finalVideoUrl ? `สั่งเรนเดอร์ใหม่ (${renderQualityPreset})` : `สั่งเรนเดอร์ Video (${renderQualityPreset})`}
                              </>
                            )}
                          </button>

                          {isRenderingFinal && (
                            <div className="space-y-1.5 pt-1">
                              <div className="w-full bg-slate-900 rounded-full h-2 overflow-hidden border border-slate-700">
                                <div
                                  className="bg-gradient-to-r from-sky-400 to-emerald-500 h-full transition-all duration-300"
                                  style={{ width: `${Math.max(5, renderProgress.percent)}%` }}
                                />
                              </div>
                              <p className="text-[10px] text-slate-400 text-center">
                                {renderProgress.phase || 'กำลังเรนเดอร์'} {renderProgress.percent}%
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Column 2: Player (Scene Final Render if available, or Scene Composition Preview) */}
              <div className="lg:col-span-4 xl:col-span-4 bg-slate-950/80 border border-slate-800 rounded-2xl p-4 sm:p-5 space-y-4">
                {/* Header with Switcher Tabs if finalVideoUrl exists */}
                <div className="flex items-center justify-between text-xs">
                  {finalVideoUrl ? (
                    <div className="flex items-center gap-1 bg-slate-900 p-1 rounded-lg border border-slate-800">
                      <button
                        onClick={() => setPlayerViewMode('final')}
                        className={`px-2.5 py-1 rounded-md font-semibold transition ${
                          playerViewMode === 'final'
                            ? 'bg-emerald-600 text-white shadow'
                            : 'text-slate-400 hover:text-white'
                        }`}
                      >
                        วิดีโอ Final Render
                      </button>
                      <button
                        onClick={() => setPlayerViewMode('preview')}
                        className={`px-2.5 py-1 rounded-md font-semibold transition ${
                          playerViewMode === 'preview'
                            ? 'bg-sky-600 text-white shadow'
                            : 'text-slate-400 hover:text-white'
                        }`}
                      >
                        ตัวเล่นพรีวิวฉาก
                      </button>
                    </div>
                  ) : (
                    <span className="font-semibold text-slate-200 flex items-center gap-1.5">
                      <Film className="w-4 h-4 text-sky-400" /> ตัวเล่นพรีวิวฉาก
                    </span>
                  )}
                  <span className="text-emerald-400 flex items-center gap-1 text-[11px]">
                    <CheckCircle className="w-3 h-3" /> 9:16 Canvas 30 fps
                  </span>
                </div>

                {/* Display Either Final Video Player OR Scene Preview Player */}
                {playerViewMode === 'preview' && previewVideoUrl ? (
                  <div className="relative aspect-[9/16] w-full max-w-[340px] mx-auto rounded-2xl overflow-hidden border border-slate-800 bg-black">
                    <video src={previewVideoUrl} controls className="w-full h-full object-contain" />
                  </div>
                ) : playerViewMode === 'final' && finalVideoUrl ? (
                  <div className="space-y-3">
                    <div className="relative aspect-[9/16] w-full max-w-[340px] mx-auto rounded-2xl overflow-hidden border border-slate-800 bg-black shadow-2xl">
                      <video
                        src={finalVideoUrl}
                        controls
                        autoPlay
                        className="w-full h-full object-contain"
                      />
                    </div>

                    <div className="p-3 bg-slate-900 rounded-xl text-xs text-slate-300 border border-slate-800 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-emerald-400 font-semibold flex items-center gap-1">
                          <CheckCircle className="w-3.5 h-3.5" /> เรนเดอร์เสร็จสมบูรณ์
                        </span>
                        <span className="text-[10px] text-slate-400 font-mono">1080 × 1920 H.264</span>
                      </div>
                      <div className="flex items-center justify-between pt-1">
                        <span className="text-[11px] text-slate-400">จัดเก็บบน Cloudflare R2</span>
                        <a
                          href={finalVideoUrl}
                          download="final_video.mp4"
                          target="_blank"
                          rel="noreferrer"
                          className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 transition shadow"
                        >
                          <Download className="w-3.5 h-3.5" /> ดาวน์โหลด MP4
                        </a>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Scene Preview Player */
                  <div className="space-y-3">
                    <div className="relative aspect-[9/16] w-full max-w-[340px] mx-auto rounded-2xl overflow-hidden border border-slate-800 bg-slate-950 shadow-2xl flex flex-col justify-between">
                      {sceneRevisions.length > 0 && (sceneRevisions[previewActiveSceneIndex]?.videoUrl || sceneRevisions[previewActiveSceneIndex]?.imageUrl) ? (
                        <>
                          {/* Active Scene Media with Motion Effect */}
                          <div className="absolute inset-0 overflow-hidden bg-black flex items-center justify-center">
                            {sceneRevisions[previewActiveSceneIndex]?.videoUrl ? (
                              <video
                                key={sceneRevisions[previewActiveSceneIndex].videoUrl}
                                src={sceneRevisions[previewActiveSceneIndex].videoUrl}
                                autoPlay={isPreviewPlaying}
                                loop
                                muted
                                playsInline
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <img
                                key={sceneRevisions[previewActiveSceneIndex].imageUrl}
                                src={sceneRevisions[previewActiveSceneIndex].imageUrl}
                                alt={`Scene ${previewActiveSceneIndex + 1}`}
                                onError={handleImageError}
                                className={`w-full h-full object-cover transition-transform duration-1000 ${
                                  isPreviewPlaying
                                    ? sceneRevisions[previewActiveSceneIndex]?.motionPreset === 'pan_left'
                                      ? 'translate-x-2 scale-105'
                                      : sceneRevisions[previewActiveSceneIndex]?.motionPreset === 'pan_right'
                                      ? '-translate-x-2 scale-105'
                                      : sceneRevisions[previewActiveSceneIndex]?.motionPreset === 'ken_burns'
                                      ? 'scale-110 translate-y-2'
                                      : 'scale-110'
                                    : 'scale-100'
                                }`}
                              />
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-black/40 pointer-events-none" />
                          </div>

                          {/* Top Overlay: Scene Index Indicator */}
                          <div className="relative z-10 p-3 sm:p-4 flex items-center justify-between">
                            <span className="px-2.5 py-1 rounded-full bg-black/60 backdrop-blur border border-white/10 text-[11px] font-semibold text-white">
                              ฉากที่ {scenes.find((s) => s.id === sceneRevisions[previewActiveSceneIndex]?.sceneId)?.orderIndex ?? previewActiveSceneIndex + 1} / {sceneRevisions.length}
                            </span>
                            <span className="px-2.5 py-1 rounded-full bg-black/60 backdrop-blur border border-white/10 text-[11px] text-slate-300">
                              {((sceneRevisions[previewActiveSceneIndex]?.timingDurationMs || 4000) / 1000).toFixed(1)}s
                            </span>
                          </div>

                          {/* Bottom Overlay: Subtitles / Caption Text */}
                          <div className="relative z-10 p-3 sm:p-4 space-y-2.5">
                            <div className="bg-black/80 backdrop-blur-md p-2.5 rounded-xl border border-white/10 shadow-lg text-center">
                              <p className="text-xs sm:text-sm font-bold text-amber-300 drop-shadow">
                                {sceneRevisions[previewActiveSceneIndex]?.captionText ||
                                  sceneRevisions[previewActiveSceneIndex]?.narration}
                              </p>
                            </div>

                            {/* Player Controls Bar */}
                            <div className="flex items-center justify-between bg-black/80 backdrop-blur rounded-xl p-1.5 border border-white/10">
                              <button
                                type="button"
                                onClick={() =>
                                  setPreviewActiveSceneIndex((prev) => Math.max(0, prev - 1))
                                }
                                disabled={previewActiveSceneIndex === 0}
                                className="p-2 text-slate-300 hover:text-white disabled:opacity-30 transition"
                                title="ฉากก่อนหน้า"
                              >
                                <SkipBack className="w-4 h-4" />
                              </button>

                              <button
                                type="button"
                                onClick={() => setIsPreviewPlaying(!isPreviewPlaying)}
                                className="p-2.5 bg-sky-500 hover:bg-sky-400 text-white rounded-full transition shadow-lg shadow-sky-500/30"
                                title={isPreviewPlaying ? 'หยุดชั่วคราว' : 'เริ่มเล่น Preview'}
                              >
                                {isPreviewPlaying ? (
                                  <Pause className="w-4 h-4 fill-current" />
                                ) : (
                                  <Play className="w-4 h-4 fill-current ml-0.5" />
                                )}
                              </button>

                              <button
                                type="button"
                                onClick={() =>
                                  setPreviewActiveSceneIndex((prev) =>
                                    Math.min(sceneRevisions.length - 1, prev + 1)
                                  )
                                }
                                disabled={previewActiveSceneIndex >= sceneRevisions.length - 1}
                                className="p-2 text-slate-300 hover:text-white disabled:opacity-30 transition"
                                title="ฉากถัดไป"
                              >
                                <SkipForward className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="m-auto text-center p-6 space-y-3">
                          <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 flex items-center justify-center mx-auto">
                            <Image className="w-6 h-6" />
                          </div>
                          <div className="space-y-1">
                            <h4 className="text-sm font-semibold text-white">ยังไม่มีสื่อภาพหรือวิดีโอสำหรับพรีวิว</h4>
                            <p className="text-xs text-slate-400 leading-relaxed">
                              กรุณาสร้างภาพหรือวิดีโอในขั้นตอนที่ 2 ก่อนเริ่มเล่นตัวอย่าง
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Column 3: Scene Inspector */}
              <div className="lg:col-span-4 xl:col-span-5 space-y-4">
                {/* Feedback Alerts for Gemini Script Generation */}
                {scriptGenError && (
                  <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/30 flex items-start gap-3 text-rose-300 text-xs">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 text-rose-400 mt-0.5" />
                    <div className="space-y-1.5 flex-1">
                      <p className="font-semibold text-rose-200">{SCRIPT_PROVIDERS.find((p) => p.id === selectedScriptProvider)?.name || 'AI'} ไม่สามารถแต่งบทได้:</p>
                      <p className="text-slate-300 bg-rose-950/40 p-2.5 rounded-lg border border-rose-800/40 font-mono text-[11px] break-all">
                        {scriptGenError}
                      </p>
                      <div className="pt-1 flex items-center gap-3">
                        <button
                          onClick={() => selectedJobId && handleTriggerGenerateScript(selectedJobId)}
                          className="px-3 py-1 bg-rose-600/60 hover:bg-rose-600 text-white rounded-lg text-xs font-medium transition"
                        >
                          ลองใหม่อีกครั้ง
                        </button>
                        <button
                          onClick={() => navigateTo('settings')}
                          className="text-sky-400 hover:text-sky-300 underline font-medium"
                        >
                          ไปที่หน้าตั้งค่าระบบ ตรวจสอบการเชื่อมต่อ &rarr;
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {scriptGenSuccess && (
                  <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-between text-emerald-300 text-xs">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                      <span>{scriptGenSuccess}</span>
                    </div>
                    <button
                      onClick={() => setScriptGenSuccess(null)}
                      className="text-slate-400 hover:text-white text-xs px-1"
                    >
                      &times;
                    </button>
                  </div>
                )}

                {/* Feedback Alerts for Image Generation */}
                {imageGenError && (
                  <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/30 flex items-start gap-3 text-rose-300 text-xs">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 text-rose-400 mt-0.5" />
                    <div className="space-y-1.5 flex-1">
                      <p className="font-semibold text-rose-200">
                        {IMAGE_PROVIDERS.find((p) => p.id === selectedImageProvider)?.name || 'ระบบสร้างภาพ'} ไม่สามารถสร้างภาพได้:
                      </p>
                      <p className="text-slate-300 bg-rose-950/40 p-2.5 rounded-lg border border-rose-800/40 font-mono text-[11px] break-all">
                        {imageGenError}
                      </p>
                      <div className="pt-1 flex items-center gap-3">
                        <button
                          onClick={() => handleGenerateAllImages()}
                          disabled={bindingReferenceSceneId !== null}
                          className="px-3 py-1 bg-rose-600/60 hover:bg-rose-600 text-white rounded-lg text-xs font-medium transition"
                        >
                          ลองใหม่อีกครั้ง
                        </button>
                        <button
                          onClick={() => navigateTo('settings')}
                          className="text-sky-400 hover:text-sky-300 underline font-medium"
                        >
                          ไปที่หน้าตั้งค่าระบบ ตรวจสอบการเชื่อมต่อ &rarr;
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {imageGenSuccess && (
                  <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-between text-emerald-300 text-xs">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                      <span>{imageGenSuccess}</span>
                    </div>
                    <button
                      onClick={() => setImageGenSuccess(null)}
                      className="text-slate-400 hover:text-white text-xs px-1"
                    >
                      &times;
                    </button>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">
                    <FileText className="w-4 h-4 text-sky-400" /> รายการฉาก
                  </h2>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-400">
                      อนุมัติแล้ว: {approvedCount} / {sceneRevisions.length} ฉาก
                    </span>
                    {sceneRevisions.length > 0 && approvedCount < sceneRevisions.length && (
                      <button
                        type="button"
                        onClick={handleApproveScenes}
                        disabled={!hasAllImages || !hasAllAudios}
                        className="px-2.5 py-1 bg-emerald-600/30 hover:bg-emerald-600 border border-emerald-500/40 text-emerald-300 hover:text-white rounded-lg text-xs font-medium transition flex items-center gap-1.5 disabled:opacity-40"
                        title={!hasAllImages || !hasAllAudios ? 'ต้องสร้างภาพและเสียงให้ครบทุกฉากก่อน' : 'อนุมัติทุกฉากทันที'}
                      >
                        <CheckCircle className="w-3.5 h-3.5" /> อนุมัติทุกฉาก
                      </button>
                    )}
                  </div>
                </div>

                {isGeneratingScript && (
                  <div className="p-8 rounded-2xl border border-indigo-500/30 bg-indigo-950/20 text-center space-y-3">
                    <Loader2 className="w-8 h-8 text-indigo-400 animate-spin mx-auto" />
                    <p className="text-sm font-semibold text-indigo-200">
                      {SCRIPT_PROVIDERS.find((p) => p.id === selectedScriptProvider)?.name || 'AI'} กำลังวิเคราะห์โจทย์และแต่งบท...
                    </p>
                    <p className="text-xs text-indigo-300/70">ระบบกำลังแต่งเสียงพากย์ภาษาไทย และสร้าง Visual Prompts ภาษาอังกฤษตามสไตล์ที่กำหนด</p>
                  </div>
                )}

                {!isGeneratingScript && sceneRevisions.length === 0 && (
                  <div className="p-8 rounded-2xl border border-dashed border-slate-700 bg-slate-900/40 text-center space-y-4">
                    <div className="w-12 h-12 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 flex items-center justify-center mx-auto">
                      <Sparkles className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-white">ยังไม่มีบทและฉากสำหรับงานนี้</h3>
                      <p className="text-xs text-slate-400 mt-1 max-w-md mx-auto">
                        ระบบยังไม่มีฉากที่สร้างขึ้น คุณสามารถเลือกผู้ให้บริการและกดปุ่มในขั้นตอนที่ 1 ทางด้านซ้าย เพื่อแต่งบทบรรยายและแบ่งฉากอัตโนมัติ
                      </p>
                    </div>
                    {selectedJobId && (
                      <button
                        onClick={() => handleTriggerGenerateScript(selectedJobId)}
                        className="px-5 py-2.5 bg-gradient-to-r from-sky-500 to-indigo-600 hover:from-sky-600 hover:to-indigo-700 text-white rounded-xl text-xs font-semibold inline-flex items-center gap-2 shadow-lg shadow-sky-500/20 transition"
                      >
                        <Sparkles className="w-4 h-4" /> ให้ {SCRIPT_PROVIDERS.find((p) => p.id === selectedScriptProvider)?.name || 'AI'} แต่งบทเดี๋ยวนี้
                      </button>
                    )}
                  </div>
                )}

                <div className="space-y-4">
                  {sceneRevisions.map((sc, index) => {
                    const isApproved = approvedSceneIds.has(sc.id);
                    const matchedScene = scenes.find((s) => s.id === sc.sceneId);
                    const sceneNumber = matchedScene?.orderIndex ?? index + 1;
                    return (
                      <div
                        key={sc.id}
                        className={`p-5 rounded-2xl border transition space-y-3 ${
                          isApproved
                            ? 'bg-emerald-950/20 border-emerald-500/40'
                            : 'bg-slate-800/40 border-slate-700/60 hover:border-slate-600'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold px-2 py-0.5 rounded bg-slate-700 text-slate-300">
                            ฉากที่ {sceneNumber}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400">
                              ระยะเวลา: {sc.timingDurationMs / 1000}s
                            </span>
                            <button
                              type="button"
                              onClick={() => handleToggleSceneApproval(sc.sceneId, sc.id)}
                              disabled={(!sc.imageUrl && !sc.videoUrl) || !sc.audioUrl}
                              title={(!sc.imageUrl && !sc.videoUrl) || !sc.audioUrl ? 'ต้องสร้างภาพหรือวิดีโอและเสียงของฉากนี้ก่อนจึงจะอนุมัติได้' : ''}
                              className={`px-3 py-1 rounded-lg text-xs font-medium flex items-center gap-1.5 transition disabled:opacity-40 ${
                                isApproved
                                  ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                                  : 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                              }`}
                            >
                              {isApproved ? (
                                <>
                                  <CheckCircle className="w-3.5 h-3.5" /> อนุมัติฉากแล้ว
                                </>
                              ) : (
                                'อนุมัติฉากนี้'
                              )}
                            </button>
                          </div>
                        </div>

                        {/* Narration */}
                        <div>
                          <label className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">
                            คำบรรยายเสียง
                          </label>
                          <p className="text-sm text-slate-200 mt-0.5 bg-slate-900/60 p-2.5 rounded-lg border border-slate-800">
                            {sc.narration}
                          </p>
                        </div>

                        {/* Narration Audio */}
                        <div className="pt-2 border-t border-slate-700/40 space-y-2">
                          <div className="flex items-center justify-between">
                            <label className="text-[11px] font-semibold text-slate-300 flex items-center gap-1.5">
                              <Mic className="w-3.5 h-3.5 text-violet-400" />
                              เสียงพากย์ฉากนี้
                            </label>
                            {sc.audioUrl && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-medium flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" /> สร้างเสียงเรียบร้อย
                              </span>
                            )}
                          </div>

                          {generatingAudioSceneId === sc.sceneId || generatingAudioSceneId === sc.id ? (
                            <div className="p-3 rounded-xl border border-violet-500/30 bg-violet-950/20 flex items-center justify-center gap-2 text-violet-300 text-xs animate-pulse">
                              <Loader2 className="w-4 h-4 animate-spin text-violet-400" />
                              <span>กำลังสร้างเสียงพากย์...</span>
                            </div>
                          ) : sc.audioUrl ? (
                            <div className="flex items-center gap-3 bg-slate-900/60 p-2.5 rounded-xl border border-slate-800">
                              <audio key={sc.audioUrl} controls src={sc.audioUrl} className="h-8 flex-1 max-w-md accent-violet-500" />
                              <button
                                type="button"
                                onClick={() => handleGenerateSceneAudio(sc.sceneId)}
                                disabled={generatingAudioSceneId !== null || isGeneratingAllAudios || !scriptApproved || isApproved || !selectedVoice}
                                className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-violet-300 rounded-lg text-xs font-medium transition flex items-center gap-1.5 border border-slate-700 flex-shrink-0"
                              >
                                <RefreshCw className="w-3 h-3" /> สร้างเสียงใหม่
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between p-2.5 bg-slate-900/40 rounded-xl border border-dashed border-slate-700">
                              <span className="text-xs text-slate-400 flex items-center gap-1.5">
                                <Volume2 className="w-3.5 h-3.5 text-slate-500" /> ยังไม่ได้สร้างเสียงพากย์สำหรับฉากนี้
                              </span>
                              <button
                                type="button"
                                onClick={() => handleGenerateSceneAudio(sc.sceneId)}
                                disabled={generatingAudioSceneId !== null || isGeneratingAllAudios || !scriptApproved || !selectedVoice}
                                className="px-3 py-1.5 bg-violet-600/80 hover:bg-violet-600 disabled:opacity-50 text-white rounded-lg text-xs font-medium transition inline-flex items-center gap-1.5 shadow-md shadow-violet-600/20"
                              >
                                <Mic className="w-3.5 h-3.5" /> {!scriptApproved ? 'สร้างเสียง (ต้องอนุมัติบทก่อน)' : 'สร้างเสียงฉากนี้'}
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Visual Prompt & Motion Preset */}
                        <div className="grid grid-cols-2 gap-3 pt-1">
                          <div>
                            <label className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">
                              คำสั่งภาพ Visual Prompt
                            </label>
                            <input
                              type="text"
                              value={sc.visualPrompt || ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                setSceneRevisions((prev) =>
                                  prev.map((item) =>
                                    item.id === sc.id ? { ...item, visualPrompt: val } : item
                                  )
                                );
                              }}
                              className="w-full mt-0.5 px-2.5 py-1.5 bg-slate-900 border border-slate-700 rounded text-xs text-slate-200 focus:outline-none focus:border-sky-500"
                            />
                          </div>

                          <div>
                            <label className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">
                              การเคลื่อนไหว Motion Preset
                            </label>
                            <select
                              value={sc.motionPreset || 'subtle_zoom'}
                              onChange={(e) => {
                                const val = e.target.value;
                                setSceneRevisions((prev) =>
                                  prev.map((item) =>
                                    item.id === sc.id ? { ...item, motionPreset: val } : item
                                  )
                                );
                              }}
                              className="w-full mt-0.5 px-2.5 py-1.5 bg-slate-900 border border-slate-700 rounded text-xs text-slate-200 focus:outline-none focus:border-sky-500"
                            >
                              <option value="subtle_zoom">Subtle Zoom ซูมเข้าช้าๆ</option>
                              <option value="ken_burns">Ken Burns แพนและซูม</option>
                              <option value="pan_left">Pan Left เลื่อนซ้าย</option>
                              <option value="pan_right">Pan Right เลื่อนขวา</option>
                            </select>
                          </div>
                        </div>

                        {/* Scene Visual Media (Image / Video / I2V) */}
                        <div className="pt-3 border-t border-slate-700/60 space-y-2">
                          <div className="flex items-center justify-between">
                            <label className="text-[11px] font-semibold text-slate-300 flex items-center gap-1.5">
                              {sc.videoUrl ? (
                                <Film className="w-3.5 h-3.5 text-indigo-400" />
                              ) : (
                                <Image className="w-3.5 h-3.5 text-sky-400" />
                              )}
                              {sc.videoUrl ? 'สื่อวิดีโอของฉาก (Video Clip)' : 'ภาพประกอบฉาก (Visual)'}
                            </label>
                            <div className="flex items-center gap-1.5">
                              {sc.videoUrl && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 font-medium">
                                  Video Clip
                                </span>
                              )}
                              {sc.imageUrl && !sc.videoUrl && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-400 border border-sky-500/20 font-medium">
                                  Still Image
                                </span>
                              )}
                              {(sc.imageUrl || sc.videoUrl) && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-medium">
                                  Cloudflare R2
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Loading States */}
                          {generatingImageSceneId === sc.sceneId || generatingImageSceneId === sc.id ? (
                            <div className="p-6 rounded-xl border border-indigo-500/30 bg-indigo-950/20 text-center space-y-2 animate-pulse">
                              <Loader2 className="w-6 h-6 text-indigo-400 animate-spin mx-auto" />
                              <p className="text-xs text-indigo-200 font-medium">
                                กำลังเรนเดอร์ภาพด้วย {selectedImageProvider === 'together' ? 'Together AI FLUX' : selectedImageProvider === 'google_flow' ? 'Google Flow Browser' : 'Local Stable Diffusion'}...
                              </p>
                            </div>
                          ) : generatingVideoSceneId === sc.sceneId || generatingVideoSceneId === sc.id ? (
                            <div className="p-6 rounded-xl border border-indigo-500/30 bg-indigo-950/20 text-center space-y-2 animate-pulse">
                              <Loader2 className="w-6 h-6 text-indigo-400 animate-spin mx-auto" />
                              <p className="text-xs text-indigo-200 font-medium">
                                กำลังเรนเดอร์คลิปวิดีโอด้วย {VIDEO_PROVIDERS.find((p) => p.id === selectedVideoProvider)?.name || 'Video AI'}...
                              </p>
                            </div>
                          ) : sc.videoUrl ? (
                            /* CASE 1: SCENE HAS VIDEO CLIP */
                            <div className="space-y-3 bg-slate-900/60 p-3 rounded-xl border border-slate-800">
                              <div className="flex gap-4 items-start">
                                <div className="w-28 h-40 rounded-lg overflow-hidden border border-slate-700 bg-slate-950 flex-shrink-0 relative group">
                                  <video
                                    key={sc.videoUrl}
                                    src={sc.videoUrl}
                                    controls
                                    playsInline
                                    className="w-full h-full object-cover"
                                  />
                                  <a
                                    href={sc.videoUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="absolute top-1 right-1 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px] opacity-0 group-hover:opacity-100 transition"
                                  >
                                    ↗
                                  </a>
                                </div>
                                <div className="flex-1 space-y-2 text-xs">
                                  <div className="flex items-center justify-between">
                                    <span className="text-[10px] text-slate-400 font-mono">
                                      {sc.videoUrl.includes('r2') || sc.videoUrl.includes('media.g-forge.site') ? 'Cloudflare R2 Video Object' : 'Video URL'}
                                    </span>
                                    <a
                                      href={sc.videoUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-[11px] text-sky-400 hover:underline font-medium"
                                    >
                                      เปิดคลิปเต็ม ↗
                                    </a>
                                  </div>

                                  {sc.motionPrompt ? (
                                    <div className="p-2 rounded bg-slate-950 border border-slate-800 space-y-1">
                                      <span className="text-[10px] text-indigo-400 font-semibold flex items-center gap-1">
                                        <Film className="w-3 h-3" /> Motion Prompt:
                                      </span>
                                      <p className="text-[11px] text-slate-300 font-mono line-clamp-3">
                                        {sc.motionPrompt}
                                      </p>
                                    </div>
                                  ) : (
                                    <p className="text-slate-300 font-mono text-[11px] line-clamp-3 bg-slate-950 p-2 rounded border border-slate-800">
                                      {sc.visualPrompt}
                                    </p>
                                  )}

                                  <SceneImageModePicker referenceUrl={sc.referenceImageUrl} provider={selectedImageProvider}
                                    value={sc.referenceImageUrl ? (sceneImageModes[sc.sceneId] || 'i2i') : 't2i'}
                                    onChange={(mode) => setSceneImageModes((prev) => ({ ...prev, [sc.sceneId]: mode }))} />
                                  <div className="flex flex-wrap items-center gap-2 pt-1">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setActiveI2VSceneId(activeI2VSceneId === sc.sceneId ? null : sc.sceneId);
                                        if (!motionPromptInputs[sc.sceneId] && sc.motionPrompt) {
                                          setMotionPromptInputs((prev) => ({ ...prev, [sc.sceneId]: sc.motionPrompt! }));
                                        }
                                      }}
                                      className="px-3 py-1.5 bg-indigo-600/30 hover:bg-indigo-600 text-indigo-300 hover:text-white rounded-lg text-xs font-medium transition flex items-center gap-1.5 border border-indigo-500/40"
                                    >
                                      <RefreshCw className="w-3 h-3" /> สร้างวิดีโอใหม่
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleGenerateSceneImage(sc.sceneId)}
                                      disabled={
                                        generatingImageSceneId !== null ||
                                        isGeneratingAllImages ||
                                        !scriptApproved || isApproved ||
                                        (selectedImageProvider === 'together' && !hasFluxConnected) ||
                                        ((selectedImageProvider === 'google_flow' || selectedImageProvider === 'gemini_web') && (!hasLocalMediaConnected || !localMediaHealth?.includes('Online'))) ||
                                        (selectedImageProvider === 'local_sd' && (!hasLocalMediaConnected || !localMediaHealth?.includes('Online') || !selectedLocalImageModel))
                                      }
                                      className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-sky-400 rounded-lg text-xs font-medium transition flex items-center gap-1.5 border border-slate-700"
                                      title="สร้างภาพนิ่งใหม่สำหรับฉากนี้"
                                    >
                                      <Image className="w-3 h-3" /> สร้างภาพนิ่งใหม่
                                    </button>
                                    {sc.imageUrl && (
                                      <a
                                        href={sc.imageUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="px-2.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-medium transition flex items-center gap-1 border border-slate-700"
                                      >
                                        <Image className="w-3 h-3" /> ดูภาพต้นฉบับ
                                      </a>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setPreviewActiveSceneIndex(index);
                                        setIsPreviewPlaying(false);
                                        setPlayerViewMode('preview');
                                      }}
                                      className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-medium transition flex items-center gap-1.5 border border-slate-700"
                                    >
                                      <Eye className="w-3 h-3" /> ดูใน Player
                                    </button>
                                  </div>
                                </div>
                              </div>

                              {/* I2V re-generation drawer */}
                              {activeI2VSceneId === sc.sceneId && (
                                <div className="p-3 bg-indigo-950/40 rounded-xl border border-indigo-500/40 space-y-2.5">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-semibold text-indigo-200 flex items-center gap-1.5">
                                      <Film className="w-3.5 h-3.5 text-indigo-400" /> ปรับแต่ง Motion Prompt & เรนเดอร์ใหม่
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => handleSuggestMotionPrompt(sc.sceneId)}
                                      disabled={suggestingMotionSceneId === sc.sceneId}
                                      className="px-2.5 py-1 bg-indigo-600/40 hover:bg-indigo-600 text-indigo-200 rounded text-[11px] font-medium transition flex items-center gap-1 border border-indigo-500/40 disabled:opacity-50"
                                    >
                                      <Sparkles className={`w-3 h-3 ${suggestingMotionSceneId === sc.sceneId ? 'animate-spin' : ''}`} />
                                      {suggestingMotionSceneId === sc.sceneId ? 'LLM กำลังคิด...' : '✨ Local LLM แนะนำ Motion'}
                                    </button>
                                  </div>
                                  <textarea
                                    rows={2}
                                    value={motionPromptInputs[sc.sceneId] ?? (sc.motionPrompt || '')}
                                    onChange={(e) => setMotionPromptInputs((prev) => ({ ...prev, [sc.sceneId]: e.target.value }))}
                                    placeholder="ระบุทิศทางมุมกล้องและการเคลื่อนไหว เช่น cinematic slow dolly in, gentle breathing, soft camera shake..."
                                    className="w-full bg-slate-950 text-slate-200 text-xs rounded-lg p-2 border border-slate-700 focus:outline-none focus:border-indigo-500 font-mono"
                                  />
                                  <SceneVideoPathPicker imageUrl={sc.imageUrl} referenceUrl={sc.referenceImageUrl}
                                    value={videoPathForScene(sc)}
                                    onChange={(path) => setSceneVideoPaths((prev) => ({ ...prev, [sc.sceneId]: path }))} />
                                  <div className="flex items-center justify-between">
                                    <span className="text-[10px] text-slate-400 font-mono">
                                      ผู้ให้บริการ: {VIDEO_PROVIDERS.find((p) => p.id === selectedVideoProvider)?.name}
                                    </span>
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => setActiveI2VSceneId(null)}
                                        className="px-2.5 py-1 text-slate-400 hover:text-slate-200 text-xs"
                                      >
                                        ปิด
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleGenerateSceneVideo(sc.sceneId)}
                                        disabled={generatingVideoSceneId === sc.sceneId}
                                        className="px-3.5 py-1 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 shadow"
                                      >
                                        <Film className="w-3 h-3" /> เรนเดอร์วิดีโอ
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : sc.imageUrl ? (
                            /* CASE 2: SCENE HAS STILL IMAGE */
                            <div className="space-y-3 bg-slate-900/60 p-3 rounded-xl border border-slate-800">
                              <div className="flex gap-4 items-start">
                                <div className="w-24 h-36 rounded-lg overflow-hidden border border-slate-700 bg-slate-950 flex-shrink-0 relative group">
                                  <img
                                    key={sc.imageUrl}
                                    src={sc.imageUrl}
                                    alt={`Scene ${sceneNumber}`}
                                    onError={handleImageError}
                                    className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                                  />
                                  <a
                                    href={sc.imageUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition text-[11px] font-medium"
                                  >
                                    ดูภาพเต็ม ↗
                                  </a>
                                </div>
                                <div className="flex-1 space-y-2 text-xs">
                                  <div className="flex items-center justify-between">
                                    <p className="text-[10px] text-slate-400 font-mono">
                                      {sc.imageUrl.includes('r2') || sc.imageUrl.includes('media.g-forge.site') ? 'Cloudflare R2 Object' : 'Visual URL'}
                                    </p>
                                  </div>
                                  <p className="text-slate-300 font-mono text-[11px] line-clamp-3 bg-slate-950 p-2 rounded border border-slate-800">
                                    {sc.visualPrompt}
                                  </p>

                                  {/* Reference Image preview if bound */}
                                  {sc.referenceImageUrl && (
                                    <div className="flex items-center gap-2.5 p-2 rounded-lg bg-slate-950/80 border border-amber-500/30 text-[11px] shadow-sm">
                                      <img
                                        src={referencePreviewUrl(sc.referenceImageUrl)}
                                        alt="Reference"
                                        onError={handleImageError}
                                        className="w-8 h-8 rounded-md object-cover border border-amber-500/50 shadow-sm shrink-0"
                                      />
                                      <div className="flex-1 min-w-0">
                                        <div className="text-amber-400 font-medium flex items-center gap-1 text-[11px]">
                                          <Sparkles className="w-3 h-3 text-amber-400 shrink-0" /> ภาพอ้างอิงสำหรับฉากนี้
                                        </div>
                                        <a
                                          href={referencePreviewUrl(sc.referenceImageUrl)}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="text-slate-400 hover:text-sky-300 hover:underline truncate block font-mono text-[10px]"
                                          title={sc.referenceImageUrl}
                                        >
                                          {sc.referenceImageUrl.split('/').pop() || sc.referenceImageUrl}
                                        </a>
                                      </div>
                                    </div>
                                  )}

                                  <div className="flex flex-wrap items-center gap-2 pt-1">
                                    <button
                                      type="button"
                                      onClick={() => handleChangeSceneVisualImage(sc.sceneId, 'clear')}
                                      disabled={updatingVisualSceneId === sc.sceneId || isApproved}
                                      className="px-3 py-1.5 bg-slate-800 hover:bg-rose-950/40 disabled:opacity-50 text-rose-400 rounded-lg text-xs font-medium transition flex items-center gap-1.5 border border-slate-700"
                                    >
                                      <Trash2 className="w-3 h-3" /> {updatingVisualSceneId === sc.sceneId ? 'กำลังลบภาพ...' : 'ลบภาพฉาก'}
                                    </button>

                                    {/* I2V Button */}
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setActiveI2VSceneId(activeI2VSceneId === sc.sceneId ? null : sc.sceneId);
                                        if (!motionPromptInputs[sc.sceneId] && sc.motionPrompt) {
                                          setMotionPromptInputs((prev) => ({ ...prev, [sc.sceneId]: sc.motionPrompt! }));
                                        }
                                      }}
                                      className="px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-sky-600 hover:from-indigo-500 hover:to-sky-500 text-white rounded-lg text-xs font-semibold transition flex items-center gap-1.5 shadow"
                                    >
                                      <Film className="w-3.5 h-3.5" /> สร้างวิดีโอ (T2V/I2V)
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => {
                                        setPreviewActiveSceneIndex(index);
                                        setIsPreviewPlaying(false);
                                        setPlayerViewMode('preview');
                                      }}
                                      className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-medium transition flex items-center gap-1.5 border border-slate-700"
                                    >
                                      <Eye className="w-3 h-3" /> ดูใน Player
                                    </button>

                                  </div>

                                </div>
                              </div>

                              {/* I2V Conversion Panel */}
                              {activeI2VSceneId === sc.sceneId && (
                                <div className="p-3 bg-indigo-950/40 rounded-xl border border-indigo-500/40 space-y-2.5 animate-fadeIn">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-semibold text-indigo-200 flex items-center gap-1.5">
                                      <Film className="w-3.5 h-3.5 text-indigo-400" /> แปลงภาพนิ่งเป็นวิดีโอ (Image-to-Video)
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => handleSuggestMotionPrompt(sc.sceneId)}
                                      disabled={suggestingMotionSceneId === sc.sceneId}
                                      className="px-2.5 py-1 bg-indigo-600/40 hover:bg-indigo-600 text-indigo-200 rounded text-[11px] font-medium transition flex items-center gap-1 border border-indigo-500/40 disabled:opacity-50"
                                    >
                                      <Sparkles className={`w-3 h-3 ${suggestingMotionSceneId === sc.sceneId ? 'animate-spin' : ''}`} />
                                      {suggestingMotionSceneId === sc.sceneId ? 'LLM กำลังคิด...' : '✨ Local LLM แนะนำ Motion'}
                                    </button>
                                  </div>

                                  <SceneVideoPathPicker imageUrl={sc.imageUrl} referenceUrl={sc.referenceImageUrl}
                                    value={videoPathForScene(sc)}
                                    onChange={(path) => setSceneVideoPaths((prev) => ({ ...prev, [sc.sceneId]: path }))} />
                                  <div className="space-y-1">
                                    <label className="text-[10px] text-slate-400">คำสั่งการเคลื่อนไหว (Motion / Camera Prompt)</label>
                                    <textarea
                                      rows={2}
                                      value={motionPromptInputs[sc.sceneId] ?? (sc.motionPrompt || '')}
                                      onChange={(e) => setMotionPromptInputs((prev) => ({ ...prev, [sc.sceneId]: e.target.value }))}
                                      placeholder="ระบุมุมกล้องและการขยับ เช่น slow pan left, cinematic gentle movement, dramatic lighting shift..."
                                      className="w-full bg-slate-950 text-slate-200 text-xs rounded-lg p-2 border border-slate-700 focus:outline-none focus:border-indigo-500 font-mono"
                                    />
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <span className="text-[10px] text-slate-400">
                                        {VIDEO_PROVIDERS.find((p) => p.id === selectedVideoProvider)?.name || selectedVideoProvider}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => setActiveI2VSceneId(null)}
                                        className="px-2.5 py-1 text-slate-400 hover:text-slate-200 text-xs"
                                      >
                                        ยกเลิก
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleGenerateSceneVideo(sc.sceneId)}
                                        disabled={generatingVideoSceneId === sc.sceneId}
                                        className="px-4 py-1.5 bg-gradient-to-r from-indigo-600 to-sky-600 hover:from-indigo-500 hover:to-sky-500 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 shadow transition disabled:opacity-50"
                                      >
                                        <Film className="w-3.5 h-3.5" /> {videoPathForScene(sc) === 't2v' ? 'เริ่มสร้างวิดีโอ (T2V)' : 'เริ่มสร้างวิดีโอ (I2V)'}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            /* CASE 3: NO VISUAL YET */
                            <div className="p-4 rounded-xl border border-dashed border-slate-700 bg-slate-900/40 space-y-3">
                              <div className="text-center space-y-1">
                                <p className="text-xs text-slate-300 font-medium">ยังไม่ได้สร้างสื่อภาพหรือวิดีโอสำหรับฉากนี้</p>
                                <p className="text-[11px] text-slate-500">
                                  เลือกสร้างภาพจากข้อความ (T2I) หรือแนบภาพจากเครื่องเป็นภาพฉากได้
                                </p>
                              </div>

                              <button type="button" disabled={bindingReferenceSceneId === sc.sceneId}
                                onClick={() => {
                                  setImageAttachmentTarget('visual');
                                  setActiveRefSceneId(activeRefSceneId === sc.sceneId && imageAttachmentTarget === 'visual' ? null : sc.sceneId);
                                }}
                                className="mx-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-sky-500/50 bg-sky-950/20 text-sky-300 text-xs hover:bg-sky-950/50">
                                <Upload className="w-3.5 h-3.5" /> แนบภาพเป็นภาพฉาก
                              </button>

                              {/* Reference Image preview if bound */}
                              {sc.referenceImageUrl ? (
                                <div className="flex items-center gap-2.5 p-2 rounded-lg bg-slate-950/80 border border-amber-500/30 text-[11px] shadow-sm text-left">
                                  <img
                                    src={referencePreviewUrl(sc.referenceImageUrl)}
                                    alt="Reference"
                                    onError={handleImageError}
                                    className="w-8 h-8 rounded-md object-cover border border-amber-500/50 shadow-sm shrink-0"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-amber-400 font-medium flex items-center gap-1 text-[11px]">
                                      <Sparkles className="w-3 h-3 text-amber-400 shrink-0" /> ภาพอ้างอิงสำหรับฉากนี้ (Reference Image)
                                    </div>
                                    <a
                                      href={referencePreviewUrl(sc.referenceImageUrl)}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-slate-400 hover:text-sky-300 hover:underline truncate block font-mono text-[10px]"
                                      title={sc.referenceImageUrl}
                                    >
                                      {sc.referenceImageUrl.split('/').pop() || sc.referenceImageUrl}
                                    </a>
                                  </div>
                                  <div className="flex items-center gap-1 shrink-0">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setImageAttachmentTarget('reference');
                                        setActiveRefSceneId(activeRefSceneId === sc.sceneId && imageAttachmentTarget === 'reference' ? null : sc.sceneId);
                                      }}
                                      className="px-2 py-0.5 text-slate-400 hover:text-sky-300 hover:bg-slate-800 rounded text-[10px] transition"
                                      title="เปลี่ยนภาพอ้างอิง"
                                      disabled={bindingReferenceSceneId === sc.sceneId}
                                    >
                                      เปลี่ยนรูป
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleSetReferenceImage(sc.sceneId, '')}
                                      className="p-1 text-slate-500 hover:text-rose-400 hover:bg-rose-950/30 rounded transition"
                                      title="ลบภาพอ้างอิง"
                                      disabled={bindingReferenceSceneId === sc.sceneId}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center justify-center">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setImageAttachmentTarget('reference');
                                      setActiveRefSceneId(activeRefSceneId === sc.sceneId && imageAttachmentTarget === 'reference' ? null : sc.sceneId);
                                    }}
                                    className="text-[11px] text-slate-400 hover:text-sky-300 transition flex items-center gap-1 px-2.5 py-1 rounded-lg border border-dashed border-slate-700 hover:border-sky-500/50 hover:bg-slate-800/60"
                                    disabled={bindingReferenceSceneId === sc.sceneId}
                                  >
                                    <PlusCircle className="w-3 h-3 text-sky-400" /> ใช้ภาพ Ref เพื่อ Gen ใหม่ (I2I)
                                  </button>
                                </div>
                              )}

                              {/* Reference image upload/picker panel */}
                              {activeRefSceneId === sc.sceneId && (
                                <div className="p-3 bg-slate-950/95 rounded-xl border border-sky-500/30 space-y-2.5 animate-fadeIn text-left">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-semibold text-sky-300 flex items-center gap-1.5">
                                      <Sparkles className="w-3.5 h-3.5 text-amber-400" /> {imageAttachmentTarget === 'visual' ? 'แนบภาพเป็นภาพฉาก' : 'แนบภาพ Ref เพื่อสร้างใหม่ (I2I)'}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => setActiveRefSceneId(null)}
                                      className="text-slate-400 hover:text-white text-xs px-1"
                                    >
                                      &times;
                                    </button>
                                  </div>

                                  {bindingReferenceSceneId === sc.sceneId && (
                                    <p role="status" className="flex items-center gap-1.5 text-xs text-sky-300">
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> กำลังเตรียมภาพ Ref...
                                    </p>
                                  )}

                                  <div className="space-y-1.5">
                                    <input
                                      type="file"
                                      id={`ref-file-case3-${sc.sceneId}`}
                                      accept="image/*"
                                      disabled={bindingReferenceSceneId !== null}
                                      className="hidden"
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) handleUploadSceneImage(sc.sceneId, file, imageAttachmentTarget);
                                        e.target.value = '';
                                      }}
                                    />
                                    <label
                                      htmlFor={`ref-file-case3-${sc.sceneId}`}
                                      className={`flex items-center justify-center gap-2 p-2.5 rounded-lg border border-dashed text-xs font-medium cursor-pointer transition select-none ${
                                        uploadingRefSceneId === sc.sceneId
                                          ? 'border-sky-500 bg-sky-950/40 text-sky-300 pointer-events-none'
                                          : 'border-slate-700 bg-slate-900/60 hover:border-sky-500 hover:bg-slate-800/60 text-slate-200'
                                      }`}
                                    >
                                      {uploadingRefSceneId === sc.sceneId ? (
                                        <>
                                          <Loader2 className="w-4 h-4 text-sky-400 animate-spin" />
                                          <span>กำลังอัปโหลดและตรวจสอบภาพ...</span>
                                        </>
                                      ) : (
                                        <>
                                          <Upload className="w-4 h-4 text-sky-400" />
                                          <span>คลิกเพื่ออัปโหลดภาพจากเครื่อง (PNG, JPG, WebP)</span>
                                        </>
                                      )}
                                    </label>
                                  </div>

                                  {uploadedFiles.length > 0 && (
                                    <div className="space-y-1 pt-1">
                                      <div className="text-[11px] text-slate-400 flex items-center gap-1">
                                        <Image className="w-3 h-3 text-emerald-400" /> หรือเลือกจากภาพสินค้า/ตัวละครในงาน:
                                      </div>
                                      <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto p-1.5 bg-slate-900/40 rounded-lg border border-slate-800">
                                        {uploadedFiles.map((asset, idx) => (
                                          <button
                                            key={idx}
                                            type="button"
                                            disabled={bindingReferenceSceneId !== null || uploadingRefSceneId !== null}
                                            onClick={() => imageAttachmentTarget === 'visual'
                                              ? handleChangeSceneVisualImage(sc.sceneId, 'attach', asset.assetId)
                                              : handleSetReferenceImage(sc.sceneId, asset.url || '', asset.assetId)}
                                            className="flex items-center gap-1.5 px-2 py-1 bg-slate-800 hover:bg-slate-700 hover:border-sky-500 border border-slate-700 rounded-md text-[11px] text-slate-200 transition"
                                            title={`ใช้ภาพ ${asset.name}`}
                                          >
                                            <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
                                            <span className="truncate max-w-[130px]">{asset.name}</span>
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {imageAttachmentTarget === 'reference' && selectedJobId && (
                                    <EarlierSceneReferencePicker jobId={selectedJobId} targetSceneId={sc.sceneId}
                                      scenes={scenes} revisions={sceneRevisions}
                                      disabled={bindingReferenceSceneId !== null || uploadingRefSceneId !== null || isGeneratingAllImages}
                                      previewUrl={referencePreviewUrl}
                                      onSelect={(sourceSceneId) => handleReuseSceneReferenceImage(sc.sceneId, sourceSceneId)} />
                                  )}
                                </div>
                              )}

                              <SceneImageModePicker referenceUrl={sc.referenceImageUrl} provider={selectedImageProvider}
                                disabled={bindingReferenceSceneId === sc.sceneId}
                                value={sc.referenceImageUrl ? (sceneImageModes[sc.sceneId] || 'i2i') : 't2i'}
                                onChange={(mode) => setSceneImageModes((prev) => ({ ...prev, [sc.sceneId]: mode }))} />
                              {/* Action Buttons: 1. สร้างภาพฉากนี้ (Still) | 2. สร้างวิดีโอฉากนี้ (Video) */}
                              <div className="flex flex-wrap items-center justify-center gap-2.5 pt-1">
                                <button
                                  type="button"
                                  onClick={() => handleGenerateSceneImage(sc.sceneId)}
                                  disabled={
                                    bindingReferenceSceneId === sc.sceneId ||
                                    generatingImageSceneId !== null ||
                                    isGeneratingAllImages ||
                                    !scriptApproved ||
                                    (selectedImageProvider === 'together' && !hasFluxConnected) ||
                                    ((selectedImageProvider === 'google_flow' || selectedImageProvider === 'gemini_web') && (!hasLocalMediaConnected || !localMediaHealth?.includes('Online'))) ||
                                    (selectedImageProvider === 'local_sd' && (!hasLocalMediaConnected || !localMediaHealth?.includes('Online') || !selectedLocalImageModel))
                                  }
                                  className="px-4 py-2 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white rounded-lg text-xs font-semibold transition inline-flex items-center gap-1.5 shadow-md shadow-sky-600/20"
                                  title={(sceneImageModes[sc.sceneId] || 'i2i') === 'i2i' && sc.referenceImageUrl ? 'สร้างภาพนิ่งด้วยภาพอ้างอิง' : 'สร้างภาพนิ่งจาก Visual Prompt'}
                                >
                                  <Image className="w-3.5 h-3.5" />
                                  {!scriptApproved
                                    ? 'สร้างภาพ (ต้องอนุมัติบทก่อน)'
                                    : sc.referenceImageUrl && (sceneImageModes[sc.sceneId] || 'i2i') === 'i2i'
                                    ? 'สร้างภาพฉากนี้ (ใช้ Ref)'
                                    : 'สร้างภาพฉากนี้ (Still)'}
                                </button>

                                <button
                                  type="button"
                                  onClick={() => {
                                    setActiveI2VSceneId(activeI2VSceneId === sc.sceneId ? null : sc.sceneId);
                                    if (!motionPromptInputs[sc.sceneId] && sc.motionPrompt) {
                                      setMotionPromptInputs((prev) => ({ ...prev, [sc.sceneId]: sc.motionPrompt! }));
                                    }
                                  }}
                                  disabled={!scriptApproved || bindingReferenceSceneId === sc.sceneId}
                                  className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-sky-600 hover:from-indigo-500 hover:to-sky-500 disabled:opacity-50 text-white rounded-lg text-xs font-semibold transition inline-flex items-center gap-1.5 shadow-md shadow-indigo-600/20"
                                  title={sc.referenceImageUrl ? 'สร้างวิดีโอ Image-to-Video จากภาพอ้างอิง' : 'สร้างวิดีโอ Text-to-Video โดยตรง'}
                                >
                                  <Film className="w-3.5 h-3.5" />
                                  {!scriptApproved
                                    ? 'สร้างวิดีโอ (ต้องอนุมัติบทก่อน)'
                                    : sc.referenceImageUrl
                                    ? 'สร้างวิดีโอ (I2V จาก Ref)'
                                    : 'สร้างวิดีโอโดยตรง (T2V)'}
                                </button>
                              </div>

                              {/* Video Generation Drawer for CASE 3 */}
                              {activeI2VSceneId === sc.sceneId && (
                                <div className="p-3 bg-indigo-950/40 rounded-xl border border-indigo-500/40 space-y-2.5 text-left animate-fadeIn">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-semibold text-indigo-200 flex items-center gap-1.5">
                                      <Film className="w-3.5 h-3.5 text-indigo-400" />
                                      {videoPathForScene(sc) === 't2v' ? 'สร้างวิดีโอจากข้อความ (T2V)' : 'สร้างวิดีโอจากภาพ (I2V)'}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => handleSuggestMotionPrompt(sc.sceneId)}
                                      disabled={suggestingMotionSceneId === sc.sceneId}
                                      className="px-2.5 py-1 bg-indigo-600/40 hover:bg-indigo-600 text-indigo-200 rounded text-[11px] font-medium transition flex items-center gap-1 border border-indigo-500/40 disabled:opacity-50"
                                    >
                                      <Sparkles className={`w-3 h-3 ${suggestingMotionSceneId === sc.sceneId ? 'animate-spin' : ''}`} />
                                      {suggestingMotionSceneId === sc.sceneId ? 'LLM กำลังคิด...' : '✨ Local LLM แนะนำ Motion'}
                                    </button>
                                  </div>

                                  <SceneVideoPathPicker imageUrl={sc.imageUrl} referenceUrl={sc.referenceImageUrl}
                                    disabled={bindingReferenceSceneId === sc.sceneId}
                                    value={videoPathForScene(sc)}
                                    onChange={(path) => setSceneVideoPaths((prev) => ({ ...prev, [sc.sceneId]: path }))} />

                                  <div className="space-y-1">
                                    <label className="text-[10px] text-slate-400">คำสั่งการเคลื่อนไหว (Motion / Camera Prompt)</label>
                                    <textarea
                                      rows={2}
                                      value={motionPromptInputs[sc.sceneId] ?? (sc.motionPrompt || '')}
                                      onChange={(e) => setMotionPromptInputs((prev) => ({ ...prev, [sc.sceneId]: e.target.value }))}
                                      placeholder="ระบุ Motion Prompt เพื่อนำไปสร้างคลิปวิดีโอ เช่น cinematic slow pan left, subtle lighting change..."
                                      className="w-full bg-slate-950 text-slate-200 text-xs rounded-lg p-2 border border-slate-700 focus:outline-none focus:border-indigo-500 font-mono"
                                    />
                                  </div>

                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <span className="text-[10px] text-slate-400">
                                        {VIDEO_PROVIDERS.find((p) => p.id === selectedVideoProvider)?.name || selectedVideoProvider}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => setActiveI2VSceneId(null)}
                                        className="px-2.5 py-1 text-slate-400 hover:text-slate-200 text-xs"
                                      >
                                        ยกเลิก
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleGenerateSceneVideo(sc.sceneId)}
                                        disabled={generatingVideoSceneId === sc.sceneId || bindingReferenceSceneId === sc.sceneId}
                                        className="px-4 py-1.5 bg-gradient-to-r from-indigo-600 to-sky-600 hover:from-indigo-500 hover:to-sky-500 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 shadow disabled:opacity-50"
                                      >
                                        <Film className="w-3.5 h-3.5" />
                                        {videoPathForScene(sc) === 't2v' ? 'เริ่มสร้างคลิปวิดีโอ (T2V)' : 'เริ่มแปลงเป็นวิดีโอ (I2V)'}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 4: SETTINGS */}
        {currentTab === 'settings' && (
          <SettingsView
            localMediaHealth={localMediaHealth}
            hasLocalMediaConnected={hasLocalMediaConnected}
            localMediaSaveStatus={localMediaSaveStatus}
            localMediaUrl={localMediaUrl}
            setLocalMediaUrl={setLocalMediaUrl}
            localMediaToken={localMediaToken}
            setLocalMediaToken={setLocalMediaToken}
            checkLocalMedia={checkLocalMedia}
            handleSaveLocalMedia={handleSaveLocalMedia}
            isSavingLocalMedia={isSavingLocalMedia}
            providerSaveStatus={providerSaveStatus}
            handleSaveProviderKey={handleSaveProviderKey}
            providerType={providerType}
            handleProviderTypeChange={handleProviderTypeChange}
            providerAlias={providerAlias}
            setProviderAlias={setProviderAlias}
            providerBaseUrl={providerBaseUrl}
            setProviderBaseUrl={setProviderBaseUrl}
            providerSecretKey={providerSecretKey}
            setProviderSecretKey={setProviderSecretKey}
            connections={connections}
            handleDeleteConnection={handleDeleteConnection}
          />
        )}
      </main>
    </div>
  );
};
export default App;
